import { api } from './emptyApi'

export const addTagTypes = [
  'Vehicles',
  'Fleets',
  'Webhook Subscriptions',
  'User Groups',
  'Users',
  'Auth0 Connection Org Mappings',
  'Organizations',
  'Vehicle Schedules',
  'Location of Interest',
  'Vehicle Telemetry',
  'Telematics Credential Validation',
  'Ticket Prefixes',
  'Vehicle Staging',
  'Asset Sharing',
  'Trips',
  'Customers',
  'EntityEvents',
  'Vehicle Faults',
  'Routes',
  'Chart',
  'Notifications',
  'Locales'
] as const
const injectedRtkApi = api
  .enhanceEndpoints({
    addTagTypes
  })
  .injectEndpoints({
    endpoints: (build) => ({
      getVehicles: build.query<GetVehiclesResponse, GetVehiclesArgs>({
        query: (queryArg) => ({
          url: `/api/vehicles`,
          params: {
            ocppTags: queryArg.ocppTags,
            activeOcppTags: queryArg.activeOcppTags,
            includeDeleted: queryArg.includeDeleted
          }
        }),
        providesTags: ['Vehicles']
      }),
      updateVehicle: build.mutation<UpdateVehicleResponse, UpdateVehicleArgs>({
        query: (queryArg) => ({ url: `/api/vehicles`, method: 'PUT', body: queryArg.editVehicleModel }),
        invalidatesTags: ['Fleets', 'Vehicles']
      }),
      createVehicleWithMetadata: build.mutation<CreateVehicleWithMetadataResponse, CreateVehicleWithMetadataArgs>({
        query: (queryArg) => ({ url: `/api/vehicles`, method: 'POST', body: queryArg.createVehicleModel }),
        invalidatesTags: ['Fleets', 'Vehicles']
      }),
      listWebhookSubscriptions: build.query<ListWebhookSubscriptionsResponse, ListWebhookSubscriptionsArgs>({
        query: (queryArg) => ({
          url: `/api/v1/webhooks`,
          params: {
            page: queryArg.page,
            count: queryArg.count,
            sort: queryArg.sort,
            enabled: queryArg.enabled
          }
        }),
        providesTags: ['Webhook Subscriptions']
      }),
      updateWebhookSubscriptions: build.mutation<UpdateWebhookSubscriptionsResponse, UpdateWebhookSubscriptionsArgs>({
        query: (queryArg) => ({ url: `/api/v1/webhooks`, method: 'PUT', body: queryArg.webhookSubscriptionModel }),
        invalidatesTags: ['Webhook Subscriptions']
      }),
      createWebhookSubscriptions: build.mutation<CreateWebhookSubscriptionsResponse, CreateWebhookSubscriptionsArgs>({
        query: (queryArg) => ({ url: `/api/v1/webhooks`, method: 'POST', body: queryArg.baseWebhookSubscriptionModel }),
        invalidatesTags: ['Webhook Subscriptions']
      }),
      getUserGroup: build.query<GetUserGroupResponse, GetUserGroupArgs>({
        query: (queryArg) => ({ url: `/api/v1/user-groups/${queryArg.id}` }),
        providesTags: ['User Groups']
      }),
      updateUserGroup: build.mutation<UpdateUserGroupResponse, UpdateUserGroupArgs>({
        query: (queryArg) => ({
          url: `/api/v1/user-groups/${queryArg.id}`,
          method: 'PUT',
          body: queryArg.userGroupUpdateParams
        }),
        invalidatesTags: ['User Groups', 'Users']
      }),
      deleteUserGroup: build.mutation<DeleteUserGroupResponse, DeleteUserGroupArgs>({
        query: (queryArg) => ({ url: `/api/v1/user-groups/${queryArg.id}`, method: 'DELETE' }),
        invalidatesTags: ['User Groups', 'Users']
      }),
      updateMapping: build.mutation<UpdateMappingResponse, UpdateMappingArgs>({
        query: (queryArg) => ({
          url: `/api/v1/auth0-connection-org-mappings/${queryArg.auth0ConnectionId}`,
          method: 'PUT',
          body: queryArg.updateAuth0ConnectionOrgMappingRequest
        }),
        invalidatesTags: ['Auth0 Connection Org Mappings']
      }),
      updateUser: build.mutation<UpdateUserResponse, UpdateUserArgs>({
        query: (queryArg) => ({ url: `/api/users`, method: 'PUT', body: queryArg.editUserModel }),
        invalidatesTags: ['Organizations', 'Users', 'Vehicles']
      }),
      createUser: build.mutation<CreateUserResponse, CreateUserArgs>({
        query: (queryArg) => ({
          url: `/api/users`,
          method: 'POST',
          body: queryArg.createUserModel,
          params: {
            isFederated: queryArg.isFederated
          }
        }),
        invalidatesTags: ['Organizations', 'Users']
      }),
      signOutUser: build.mutation<SignOutUserResponse, SignOutUserArgs>({
        query: (queryArg) => ({ url: `/api/users/${queryArg.id}/signout`, method: 'PUT' }),
        invalidatesTags: ['Users']
      }),
      resend: build.mutation<ResendResponse, ResendArgs>({
        query: (queryArg) => ({ url: `/api/users/${queryArg.id}/resend`, method: 'PUT' }),
        invalidatesTags: ['Users']
      }),
      resetPassword: build.mutation<ResetPasswordResponse, ResetPasswordArgs>({
        query: (queryArg) => ({ url: `/api/users/${queryArg.id}/password`, method: 'PUT' }),
        invalidatesTags: ['Users']
      }),
      moveUser: build.mutation<MoveUserResponse, MoveUserArgs>({
        query: (queryArg) => ({ url: `/api/users/${queryArg.id}/move`, method: 'PUT', body: queryArg.moveUserModel }),
        invalidatesTags: ['Organizations', 'Users']
      }),
      enableUser: build.mutation<EnableUserResponse, EnableUserArgs>({
        query: (queryArg) => ({ url: `/api/users/${queryArg.id}/enable`, method: 'PUT' }),
        invalidatesTags: ['Users']
      }),
      disableUser: build.mutation<DisableUserResponse, DisableUserArgs>({
        query: (queryArg) => ({ url: `/api/users/${queryArg.id}/disable`, method: 'PUT' }),
        invalidatesTags: ['Users']
      }),
      updateScheduledRoute: build.mutation<UpdateScheduledRouteResponse, UpdateScheduledRouteArgs>({
        query: (queryArg) => ({
          url: `/api/scheduled-routes/${queryArg.scheduledRouteId}`,
          method: 'PUT',
          body: queryArg.scheduledRouteUpdateModel
        }),
        invalidatesTags: ['Vehicle Schedules']
      }),
      cancelScheduledRoute: build.mutation<CancelScheduledRouteResponse, CancelScheduledRouteArgs>({
        query: (queryArg) => ({ url: `/api/scheduled-routes/${queryArg.scheduledRouteId}`, method: 'DELETE' }),
        invalidatesTags: ['Vehicle Schedules']
      }),
      updateOrganization: build.mutation<UpdateOrganizationResponse, UpdateOrganizationArgs>({
        query: (queryArg) => ({ url: `/api/organizations`, method: 'PUT', body: queryArg.editOrganizationModel }),
        invalidatesTags: ['Organizations', 'Users']
      }),
      updateFleetTerms: build.mutation<UpdateFleetTermsResponse, UpdateFleetTermsArgs>({
        query: (queryArg) => ({ url: `/api/organizations/terms`, method: 'PUT', body: queryArg.organizationTerms }),
        invalidatesTags: ['Organizations']
      }),
      saveFleetTerms: build.mutation<SaveFleetTermsResponse, SaveFleetTermsArgs>({
        query: (queryArg) => ({ url: `/api/organizations/terms`, method: 'POST', body: queryArg.organizationTerms }),
        invalidatesTags: ['Organizations']
      }),
      getLocationsOfInterestForOrganization: build.query<
        GetLocationsOfInterestForOrganizationResponse,
        GetLocationsOfInterestForOrganizationArgs
      >({
        query: (queryArg) => ({
          url: `/api/locations`,
          params: {
            organizationId: queryArg.organizationId
          }
        }),
        providesTags: ['Location of Interest']
      }),
      updateLocationOfInterest: build.mutation<UpdateLocationOfInterestResponse, UpdateLocationOfInterestArgs>({
        query: (queryArg) => ({ url: `/api/locations`, method: 'PUT', body: queryArg.locationOfInterestUpdateModel }),
        invalidatesTags: ['Location of Interest']
      }),
      createLocationOfInterest: build.mutation<CreateLocationOfInterestResponse, CreateLocationOfInterestArgs>({
        query: (queryArg) => ({ url: `/api/locations`, method: 'POST', body: queryArg.locationOfInterestCreateModel }),
        invalidatesTags: ['Location of Interest']
      }),
      getFleets: build.query<GetFleetsResponse, GetFleetsArgs>({
        query: (queryArg) => ({
          url: `/api/fleets`,
          params: {
            page: queryArg.page,
            count: queryArg.count,
            sort: queryArg.sort,
            organizationId: queryArg.organizationId
          }
        }),
        providesTags: ['Fleets']
      }),
      updateFleet: build.mutation<UpdateFleetResponse, UpdateFleetArgs>({
        query: (queryArg) => ({ url: `/api/fleets`, method: 'PUT', body: queryArg.fleetModel }),
        invalidatesTags: ['Fleets', 'Organizations', 'Vehicles']
      }),
      createFleet: build.mutation<CreateFleetResponse, CreateFleetArgs>({
        query: (queryArg) => ({ url: `/api/fleets`, method: 'POST', body: queryArg.createFleetModel }),
        invalidatesTags: ['Fleets', 'Organizations', 'Vehicles']
      }),
      addVehicleEvent: build.mutation<AddVehicleEventResponse, AddVehicleEventArgs>({
        query: (queryArg) => ({ url: `/api/vehicles/events`, method: 'POST', body: queryArg.vehicleEvent }),
        invalidatesTags: ['Vehicles']
      }),
      updateVehicleTelemetry: build.mutation<UpdateVehicleTelemetryResponse, UpdateVehicleTelemetryArgs>({
        query: (queryArg) => ({
          url: `/api/v1/vehicle-telemetry`,
          method: 'POST',
          body: queryArg.vehicleTelemetryCreateParams
        }),
        invalidatesTags: ['Vehicle Telemetry']
      }),
      validateTelematicsCredentials: build.mutation<
        ValidateTelematicsCredentialsResponse,
        ValidateTelematicsCredentialsArgs
      >({
        query: (queryArg) => ({
          url: `/api/v1/validate-telematics-credentials`,
          method: 'POST',
          body: queryArg.telematicsCredentialValidationRequest
        }),
        invalidatesTags: ['Telematics Credential Validation']
      }),
      getUserGroups: build.query<GetUserGroupsResponse, GetUserGroupsArgs>({
        query: (queryArg) => ({
          url: `/api/v1/user-groups`,
          params: {
            organizationId: queryArg.organizationId
          }
        }),
        providesTags: ['User Groups']
      }),
      createUserGroup: build.mutation<CreateUserGroupResponse, CreateUserGroupArgs>({
        query: (queryArg) => ({ url: `/api/v1/user-groups`, method: 'POST', body: queryArg.userGroupCreateParams }),
        invalidatesTags: ['User Groups', 'Users']
      }),
      backfillTicketPrefixes: build.mutation<BackfillTicketPrefixesResponse, BackfillTicketPrefixesArgs>({
        query: (queryArg) => ({
          url: `/api/v1/ticket-prefixes/backfill`,
          method: 'POST',
          params: {
            page: queryArg.page,
            count: queryArg.count
          }
        }),
        invalidatesTags: ['Ticket Prefixes']
      }),
      getStagedVehicles: build.query<GetStagedVehiclesResponse, GetStagedVehiclesArgs>({
        query: (queryArg) => ({
          url: `/api/v1/stage-vehicles`,
          params: {
            integrationIds: queryArg.integrationIds,
            page: queryArg.page,
            count: queryArg.count,
            sort: queryArg.sort
          }
        }),
        providesTags: ['Vehicle Staging']
      }),
      stageVehicles: build.mutation<StageVehiclesResponse, StageVehiclesArgs>({
        query: (queryArg) => ({
          url: `/api/v1/stage-vehicles`,
          method: 'POST',
          params: {
            integrationId: queryArg.integrationId
          }
        }),
        invalidatesTags: ['Vehicle Staging']
      }),
      revokeSharing: build.mutation<RevokeSharingResponse, RevokeSharingArgs>({
        query: (queryArg) => ({
          url: `/api/v1/organizations/${queryArg.id}/sharing/${queryArg.sharingId}/revoke`,
          method: 'POST'
        }),
        invalidatesTags: ['Asset Sharing']
      }),
      rejectOffer: build.mutation<RejectOfferResponse, RejectOfferArgs>({
        query: (queryArg) => ({
          url: `/api/v1/organizations/${queryArg.id}/sharing/${queryArg.sharingId}/reject`,
          method: 'POST'
        }),
        invalidatesTags: ['Asset Sharing']
      }),
      acceptOffer: build.mutation<AcceptOfferResponse, AcceptOfferArgs>({
        query: (queryArg) => ({
          url: `/api/v1/organizations/${queryArg.id}/sharing/${queryArg.sharingId}/accept`,
          method: 'POST'
        }),
        invalidatesTags: ['Asset Sharing']
      }),
      revokeInviteCode: build.mutation<RevokeInviteCodeResponse, RevokeInviteCodeArgs>({
        query: (queryArg) => ({
          url: `/api/v1/organizations/${queryArg.id}/sharing/invite/${queryArg.inviteCodeId}/revoke`,
          method: 'POST'
        }),
        invalidatesTags: ['Asset Sharing']
      }),
      offerOrgs: build.mutation<OfferOrgsResponse, OfferOrgsArgs>({
        query: (queryArg) => ({
          url: `/api/v1/organizations/${queryArg.id}/sharing/invite/${queryArg.inviteCodeId}/offer`,
          method: 'POST',
          body: queryArg.offerOrgsRequestModel
        }),
        invalidatesTags: ['Asset Sharing']
      }),
      redeemInviteCode: build.mutation<RedeemInviteCodeResponse, RedeemInviteCodeArgs>({
        query: (queryArg) => ({
          url: `/api/v1/organizations/${queryArg.id}/sharing/invite/redeem`,
          method: 'POST',
          body: queryArg.redeemCodeRequestModel
        }),
        invalidatesTags: ['Asset Sharing']
      }),
      generateInviteCode: build.mutation<GenerateInviteCodeResponse, GenerateInviteCodeArgs>({
        query: (queryArg) => ({ url: `/api/v1/organizations/${queryArg.id}/sharing/invite/generate`, method: 'POST' }),
        invalidatesTags: ['Asset Sharing']
      }),
      saveOrganizationLogoVariant: build.mutation<SaveOrganizationLogoVariantResponse, SaveOrganizationLogoVariantArgs>(
        {
          query: (queryArg) => ({
            url: `/api/v1/organizations/${queryArg.id}/logos/${queryArg.variant}`,
            method: 'POST',
            body: queryArg.body
          }),
          invalidatesTags: ['Organizations']
        }
      ),
      deleteOrganizationLogoVariant: build.mutation<
        DeleteOrganizationLogoVariantResponse,
        DeleteOrganizationLogoVariantArgs
      >({
        query: (queryArg) => ({
          url: `/api/v1/organizations/${queryArg.id}/logos/${queryArg.variant}`,
          method: 'DELETE'
        }),
        invalidatesTags: ['Organizations']
      }),
      saveOrganizationIconVariant: build.mutation<SaveOrganizationIconVariantResponse, SaveOrganizationIconVariantArgs>(
        {
          query: (queryArg) => ({
            url: `/api/v1/organizations/${queryArg.id}/icons/${queryArg.variant}`,
            method: 'POST',
            body: queryArg.body
          }),
          invalidatesTags: ['Organizations']
        }
      ),
      deleteOrganizationIconVariant: build.mutation<
        DeleteOrganizationIconVariantResponse,
        DeleteOrganizationIconVariantArgs
      >({
        query: (queryArg) => ({
          url: `/api/v1/organizations/${queryArg.id}/icons/${queryArg.variant}`,
          method: 'DELETE'
        }),
        invalidatesTags: ['Organizations']
      }),
      onboardVehicles: build.mutation<OnboardVehiclesResponse, OnboardVehiclesArgs>({
        query: (queryArg) => ({
          url: `/api/v1/onboard-vehicles`,
          method: 'POST',
          body: queryArg.onboardVehiclesRequest
        }),
        invalidatesTags: ['Vehicle Staging']
      }),
      createMapping: build.mutation<CreateMappingResponse, CreateMappingArgs>({
        query: (queryArg) => ({
          url: `/api/v1/auth0-connection-org-mappings`,
          method: 'POST',
          body: queryArg.createAuth0ConnectionOrgMappingRequest
        }),
        invalidatesTags: ['Auth0 Connection Org Mappings']
      }),
      initiateMfaSetup: build.query<InitiateMfaSetupResponse, InitiateMfaSetupArgs>({
        query: () => ({ url: `/api/users/setup-mfa` }),
        providesTags: ['Users']
      }),
      verifySoftwareToken: build.mutation<VerifySoftwareTokenResponse, VerifySoftwareTokenArgs>({
        query: (queryArg) => ({
          url: `/api/users/setup-mfa`,
          method: 'POST',
          params: {
            userCode: queryArg.userCode
          }
        }),
        invalidatesTags: ['Users']
      }),
      lookupByAuthProvider: build.mutation<LookupByAuthProviderResponse, LookupByAuthProviderArgs>({
        query: (queryArg) => ({
          url: `/api/users/lookup-by-auth-provider`,
          method: 'POST',
          body: queryArg.authProviderLookupRequest
        }),
        invalidatesTags: ['Users']
      }),
      getOrCreateUserIdentity: build.mutation<GetOrCreateUserIdentityResponse, GetOrCreateUserIdentityArgs>({
        query: (queryArg) => ({ url: `/api/users/identity`, method: 'POST', body: queryArg.userIdentityRequest }),
        invalidatesTags: ['Users']
      }),
      createSimTrip: build.mutation<CreateSimTripResponse, CreateSimTripArgs>({
        query: (queryArg) => ({ url: `/api/trips`, method: 'POST', body: queryArg.simTripCreateParams }),
        invalidatesTags: ['Trips']
      }),
      createTripNote: build.mutation<CreateTripNoteResponse, CreateTripNoteArgs>({
        query: (queryArg) => ({ url: `/api/trips/notes`, method: 'POST', body: queryArg.tripNote }),
        invalidatesTags: ['Trips']
      }),
      getScheduledRoutes: build.query<GetScheduledRoutesResponse, GetScheduledRoutesArgs>({
        query: (queryArg) => ({
          url: `/api/scheduled-routes`,
          params: {
            vehicleId: queryArg.vehicleId,
            siteId: queryArg.siteId,
            ocppTag: queryArg.ocppTag,
            scheduledRouteType: queryArg.scheduledRouteType,
            departFrom: queryArg.departFrom,
            departTo: queryArg.departTo,
            arriveFrom: queryArg.arriveFrom,
            arriveTo: queryArg.arriveTo,
            page: queryArg.page,
            size: queryArg.size
          }
        }),
        providesTags: ['Vehicle Schedules']
      }),
      createScheduledRoute: build.mutation<CreateScheduledRouteResponse, CreateScheduledRouteArgs>({
        query: (queryArg) => ({ url: `/api/scheduled-routes`, method: 'POST', body: queryArg.scheduleRouteRequest }),
        invalidatesTags: ['Vehicle Schedules']
      }),
      assignVehicle: build.mutation<AssignVehicleResponse, AssignVehicleArgs>({
        query: (queryArg) => ({
          url: `/api/organizations/${queryArg.id}/vehicles/${queryArg.vehicleId}/assign`,
          method: 'POST'
        }),
        invalidatesTags: ['Organizations']
      }),
      onboardOrganizationToStripe: build.mutation<OnboardOrganizationToStripeResponse, OnboardOrganizationToStripeArgs>(
        {
          query: (queryArg) => ({
            url: `/api/organizations/${queryArg.id}/stripe/onboard`,
            method: 'POST',
            body: queryArg.urlRedirectModel
          }),
          invalidatesTags: ['Organizations']
        }
      ),
      getSsoConnectionDetails: build.query<GetSsoConnectionDetailsResponse, GetSsoConnectionDetailsArgs>({
        query: (queryArg) => ({ url: `/api/organizations/${queryArg.id}/sso-config` }),
        providesTags: ['Organizations']
      }),
      createSsoConfigurationTicket: build.mutation<
        CreateSsoConfigurationTicketResponse,
        CreateSsoConfigurationTicketArgs
      >({
        query: (queryArg) => ({
          url: `/api/organizations/${queryArg.id}/sso-config`,
          method: 'POST',
          params: {
            allowedDomains: queryArg.allowedDomains,
            idpInitiatedEnabled: queryArg.idpInitiatedEnabled,
            displayName: queryArg.displayName
          }
        }),
        invalidatesTags: ['Organizations']
      }),
      deleteSsoConfiguration: build.mutation<DeleteSsoConfigurationResponse, DeleteSsoConfigurationArgs>({
        query: (queryArg) => ({ url: `/api/organizations/${queryArg.id}/sso-config`, method: 'DELETE' }),
        invalidatesTags: ['Organizations']
      }),
      transferSsoConfiguration: build.mutation<TransferSsoConfigurationResponse, TransferSsoConfigurationArgs>({
        query: (queryArg) => ({
          url: `/api/organizations/${queryArg.id}/sso-config/transfer`,
          method: 'POST',
          body: queryArg.transferSsoConfigRequest
        }),
        invalidatesTags: ['Organizations']
      }),
      saveOrganizationLogo: build.mutation<SaveOrganizationLogoResponse, SaveOrganizationLogoArgs>({
        query: (queryArg) => ({ url: `/api/organizations/${queryArg.id}/logo`, method: 'POST', body: queryArg.body }),
        invalidatesTags: ['Organizations']
      }),
      deleteOrganizationLogo: build.mutation<DeleteOrganizationLogoResponse, DeleteOrganizationLogoArgs>({
        query: (queryArg) => ({ url: `/api/organizations/${queryArg.id}/logo`, method: 'DELETE' }),
        invalidatesTags: ['Organizations']
      }),
      getApiKeysByOrganizationId: build.query<GetApiKeysByOrganizationIdResponse, GetApiKeysByOrganizationIdArgs>({
        query: (queryArg) => ({ url: `/api/organizations/${queryArg.id}/keys` }),
        providesTags: ['Organizations']
      }),
      provisionApiKeyByOrganizationId: build.mutation<
        ProvisionApiKeyByOrganizationIdResponse,
        ProvisionApiKeyByOrganizationIdArgs
      >({
        query: (queryArg) => ({
          url: `/api/organizations/${queryArg.id}/keys`,
          method: 'POST',
          body: queryArg.organizationApiKeyCreateParams
        }),
        invalidatesTags: ['Organizations']
      }),
      getIntegrationsByOrganizationId: build.query<
        GetIntegrationsByOrganizationIdResponse,
        GetIntegrationsByOrganizationIdArgs
      >({
        query: (queryArg) => ({ url: `/api/organizations/${queryArg.id}/integrations` }),
        providesTags: ['Organizations']
      }),
      saveOrganizationIntegration: build.mutation<SaveOrganizationIntegrationResponse, SaveOrganizationIntegrationArgs>(
        {
          query: (queryArg) => ({
            url: `/api/organizations/${queryArg.id}/integrations`,
            method: 'POST',
            body: queryArg.organizationIntegration
          }),
          invalidatesTags: ['Organizations']
        }
      ),
      saveOrganizationIcon: build.mutation<SaveOrganizationIconResponse, SaveOrganizationIconArgs>({
        query: (queryArg) => ({ url: `/api/organizations/${queryArg.id}/icon`, method: 'POST', body: queryArg.body }),
        invalidatesTags: ['Organizations']
      }),
      deleteOrganizationIcon: build.mutation<DeleteOrganizationIconResponse, DeleteOrganizationIconArgs>({
        query: (queryArg) => ({ url: `/api/organizations/${queryArg.id}/icon`, method: 'DELETE' }),
        invalidatesTags: ['Organizations']
      }),
      getOrganizationCustomers: build.query<GetOrganizationCustomersResponse, GetOrganizationCustomersArgs>({
        query: (queryArg) => ({
          url: `/api/organizations/${queryArg.id}/customers`,
          params: {
            skipEnhanceData: queryArg.skipEnhanceData
          }
        }),
        providesTags: ['Organizations']
      }),
      createOrganization: build.mutation<CreateOrganizationResponse, CreateOrganizationArgs>({
        query: (queryArg) => ({
          url: `/api/organizations/${queryArg.id}/customers`,
          method: 'POST',
          body: queryArg.createOrganizationModel
        }),
        invalidatesTags: ['Customers', 'Organizations']
      }),
      publishToSns: build.mutation<PublishToSnsResponse, PublishToSnsArgs>({
        query: (queryArg) => ({
          url: `/api/events`,
          method: 'POST',
          body: queryArg.body,
          params: {
            subEntity: queryArg.subEntity,
            event: queryArg.event
          }
        }),
        invalidatesTags: ['EntityEvents']
      }),
      createChargePort: build.mutation<CreateChargePortResponse, CreateChargePortArgs>({
        query: (queryArg) => ({ url: `/api/charge-ports`, method: 'POST', body: queryArg.createChargePortModel }),
        invalidatesTags: ['Fleets', 'Vehicles']
      }),
      assignVehicleToBillingOrganization: build.mutation<
        AssignVehicleToBillingOrganizationResponse,
        AssignVehicleToBillingOrganizationArgs
      >({
        query: (queryArg) => ({
          url: `/api/billing/organizations/${queryArg.id}/vehicles/${queryArg.vehicleId}/assign`,
          method: 'POST'
        }),
        invalidatesTags: ['Organizations']
      }),
      patchOrganizationFeatures: build.mutation<PatchOrganizationFeaturesResponse, PatchOrganizationFeaturesArgs>({
        query: (queryArg) => ({
          url: `/api/v1/organizations/${queryArg.id}/features`,
          method: 'PATCH',
          body: queryArg.patchOrganizationFeaturesModel
        }),
        invalidatesTags: ['Organizations']
      }),
      getVehicle: build.query<GetVehicleResponse, GetVehicleArgs>({
        query: (queryArg) => ({
          url: `/api/vehicles/${queryArg.id}`,
          params: {
            includeDeleted: queryArg.includeDeleted
          }
        }),
        providesTags: ['Vehicles']
      }),
      getLatestVehicleWeatherForecast: build.query<
        GetLatestVehicleWeatherForecastResponse,
        GetLatestVehicleWeatherForecastArgs
      >({
        query: (queryArg) => ({
          url: `/api/vehicles/${queryArg.id}/weather`,
          params: {
            includeDeleted: queryArg.includeDeleted
          }
        }),
        providesTags: ['Vehicles']
      }),
      getTrips: build.query<GetTripsResponse, GetTripsArgs>({
        query: (queryArg) => ({
          url: `/api/vehicles/${queryArg.id}/trips`,
          params: {
            from: queryArg['from'],
            to: queryArg.to,
            source: queryArg.source
          }
        }),
        providesTags: ['Vehicles']
      }),
      getTrip: build.query<GetTripResponse, GetTripArgs>({
        query: (queryArg) => ({ url: `/api/vehicles/${queryArg.id}/trips/${queryArg.tripId}` }),
        providesTags: ['Vehicles']
      }),
      getVehicleTelematics: build.query<GetVehicleTelematicsResponse, GetVehicleTelematicsArgs>({
        query: (queryArg) => ({
          url: `/api/vehicles/${queryArg.id}/telematics`,
          params: {
            from: queryArg['from'],
            to: queryArg.to
          }
        }),
        providesTags: ['Vehicles']
      }),
      getServiceableRangeForVehicle: build.query<
        GetServiceableRangeForVehicleResponse,
        GetServiceableRangeForVehicleArgs
      >({
        query: (queryArg) => ({
          url: `/api/vehicles/${queryArg.id}/range`,
          params: {
            includeDeleted: queryArg.includeDeleted
          }
        }),
        providesTags: ['Vehicles']
      }),
      getMetricsForVehicle: build.query<GetMetricsForVehicleResponse, GetMetricsForVehicleArgs>({
        query: (queryArg) => ({
          url: `/api/vehicles/${queryArg.id}/metrics`,
          params: {
            date: queryArg.date,
            includeDeleted: queryArg.includeDeleted
          }
        }),
        providesTags: ['Vehicles']
      }),
      getVehicleFaults: build.query<GetVehicleFaultsResponse, GetVehicleFaultsArgs>({
        query: (queryArg) => ({
          url: `/api/vehicles/${queryArg.id}/faults`,
          params: {
            page: queryArg.page,
            count: queryArg.count,
            sort: queryArg.sort,
            searchString: queryArg.searchString,
            from: queryArg['from'],
            to: queryArg.to
          }
        }),
        providesTags: ['Vehicle Faults']
      }),
      getAssetEvents: build.query<GetAssetEventsResponse, GetAssetEventsArgs>({
        query: (queryArg) => ({
          url: `/api/vehicles/${queryArg.id}/events/`,
          params: {
            type: queryArg['type'],
            from: queryArg['from'],
            to: queryArg.to
          }
        }),
        providesTags: ['Vehicles']
      }),
      getVehicleStatusBreakdownForVehicles: build.query<
        GetVehicleStatusBreakdownForVehiclesResponse,
        GetVehicleStatusBreakdownForVehiclesArgs
      >({
        query: (queryArg) => ({
          url: `/api/vehicles/vehicle-status-timespan-data`,
          params: {
            from: queryArg['from'],
            to: queryArg.to,
            organizationId: queryArg.organizationId,
            fleetId: queryArg.fleetId,
            includeDeleted: queryArg.includeDeleted
          }
        }),
        providesTags: ['Vehicles']
      }),
      getTimespanDataForVehicles: build.query<GetTimespanDataForVehiclesResponse, GetTimespanDataForVehiclesArgs>({
        query: (queryArg) => ({
          url: `/api/vehicles/timespan-data`,
          params: {
            from: queryArg['from'],
            to: queryArg.to,
            organizationId: queryArg.organizationId,
            fleetId: queryArg.fleetId,
            includeDeleted: queryArg.includeDeleted
          }
        }),
        providesTags: ['Vehicles']
      }),
      getVehiclesBySite: build.query<GetVehiclesBySiteResponse, GetVehiclesBySiteArgs>({
        query: (queryArg) => ({
          url: `/api/vehicles/site/${queryArg.id}`,
          params: {
            includeDeleted: queryArg.includeDeleted
          }
        }),
        providesTags: ['Vehicles']
      }),
      getVehicleReport: build.query<GetVehicleReportResponse, GetVehicleReportArgs>({
        query: (queryArg) => ({
          url: `/api/vehicles/report`,
          params: {
            from: queryArg['from'],
            to: queryArg.to,
            organizationId: queryArg.organizationId,
            fleetId: queryArg.fleetId,
            siteId: queryArg.siteId,
            includeDeleted: queryArg.includeDeleted
          }
        }),
        providesTags: ['Vehicles']
      }),
      getLatestVehicleStatuses: build.query<GetLatestVehicleStatusesResponse, GetLatestVehicleStatusesArgs>({
        query: (queryArg) => ({
          url: `/api/vehicles/latest-statuses`,
          params: {
            siteId: queryArg.siteId,
            includeDeleted: queryArg.includeDeleted
          }
        }),
        providesTags: ['Vehicles']
      }),
      getMetricsForVehicles: build.query<GetMetricsForVehiclesResponse, GetMetricsForVehiclesArgs>({
        query: (queryArg) => ({
          url: `/api/vehicles/aggregate-timespan-data`,
          params: {
            from: queryArg['from'],
            to: queryArg.to,
            organizationId: queryArg.organizationId,
            fleetId: queryArg.fleetId,
            includeDeleted: queryArg.includeDeleted
          }
        }),
        providesTags: ['Vehicles']
      }),
      getVehicleAuthTags: build.query<GetVehicleAuthTagsResponse, GetVehicleAuthTagsArgs>({
        query: (queryArg) => ({
          url: `/api/vehicle-auth-tags`,
          params: {
            vehicleIds: queryArg.vehicleIds,
            page: queryArg.page,
            count: queryArg.count,
            sort: queryArg.sort,
            from: queryArg['from'],
            to: queryArg.to
          }
        }),
        providesTags: ['Vehicles']
      }),
      listWebhookSubscriptions1: build.query<ListWebhookSubscriptions1Response, ListWebhookSubscriptions1Args>({
        query: (queryArg) => ({
          url: `/api/v2/webhooks`,
          params: {
            page: queryArg.page,
            count: queryArg.count,
            sort: queryArg.sort,
            enabled: queryArg.enabled
          }
        }),
        providesTags: ['Webhook Subscriptions']
      }),
      getWebhookSubscription: build.query<GetWebhookSubscriptionResponse, GetWebhookSubscriptionArgs>({
        query: (queryArg) => ({ url: `/api/v2/webhooks/${queryArg.id}` }),
        providesTags: ['Webhook Subscriptions']
      }),
      getWebhookSubscription1: build.query<GetWebhookSubscription1Response, GetWebhookSubscription1Args>({
        query: (queryArg) => ({ url: `/api/v1/webhooks/${queryArg.id}` }),
        providesTags: ['Webhook Subscriptions']
      }),
      deleteWebhookSubscription: build.mutation<DeleteWebhookSubscriptionResponse, DeleteWebhookSubscriptionArgs>({
        query: (queryArg) => ({ url: `/api/v1/webhooks/${queryArg.id}`, method: 'DELETE' }),
        invalidatesTags: ['Webhook Subscriptions']
      }),
      getVehicleFaults1: build.query<GetVehicleFaults1Response, GetVehicleFaults1Args>({
        query: (queryArg) => ({
          url: `/api/v1/vehicle-faults`,
          params: {
            vehicleId: queryArg.vehicleId,
            page: queryArg.page,
            count: queryArg.count,
            sort: queryArg.sort,
            from: queryArg['from'],
            to: queryArg.to
          }
        }),
        providesTags: ['Vehicle Faults']
      }),
      getUsers: build.query<GetUsersResponse, GetUsersArgs>({
        query: (queryArg) => ({
          url: `/api/v1/users`,
          params: {
            page: queryArg.page,
            count: queryArg.count,
            sort: queryArg.sort,
            depth: queryArg.depth
          }
        }),
        providesTags: ['Users']
      }),
      getDescartesRouteById: build.query<GetDescartesRouteByIdResponse, GetDescartesRouteByIdArgs>({
        query: (queryArg) => ({ url: `/api/v1/routes/descartes/${queryArg.id}` }),
        providesTags: ['Routes']
      }),
      getVehiclesForOrganizationV1: build.query<GetVehiclesForOrganizationV1Response, GetVehiclesForOrganizationV1Args>(
        {
          query: (queryArg) => ({
            url: `/api/v1/organizations/${queryArg.id}/vehicles`,
            params: {
              page: queryArg.page,
              count: queryArg.count,
              sort: queryArg.sort,
              ocppTags: queryArg.ocppTags,
              siteIds: queryArg.siteIds,
              includeDeleted: queryArg.includeDeleted
            }
          }),
          providesTags: ['Organizations', 'Vehicles']
        }
      ),
      getActiveCodes: build.query<GetActiveCodesResponse, GetActiveCodesArgs>({
        query: (queryArg) => ({ url: `/api/v1/organizations/${queryArg.id}/sharing/invite/active` }),
        providesTags: ['Asset Sharing']
      }),
      getGrantorShares: build.query<GetGrantorSharesResponse, GetGrantorSharesArgs>({
        query: (queryArg) => ({ url: `/api/v1/organizations/${queryArg.id}/sharing/grantor` }),
        providesTags: ['Asset Sharing']
      }),
      getGrantorPending: build.query<GetGrantorPendingResponse, GetGrantorPendingArgs>({
        query: (queryArg) => ({ url: `/api/v1/organizations/${queryArg.id}/sharing/grantor/pending` }),
        providesTags: ['Asset Sharing']
      }),
      getGranteeShares: build.query<GetGranteeSharesResponse, GetGranteeSharesArgs>({
        query: (queryArg) => ({ url: `/api/v1/organizations/${queryArg.id}/sharing/grantee` }),
        providesTags: ['Asset Sharing']
      }),
      getSharingAgreements: build.query<GetSharingAgreementsResponse, GetSharingAgreementsArgs>({
        query: (queryArg) => ({ url: `/api/v1/organizations/${queryArg.id}/sharing/agreements` }),
        providesTags: ['Asset Sharing']
      }),
      getChartValuesV1: build.query<GetChartValuesV1Response, GetChartValuesV1Args>({
        query: (queryArg) => ({
          url: `/api/v1/chart/${queryArg.id}`,
          params: {
            from: queryArg['from'],
            to: queryArg.to,
            attributes: queryArg.attributes
          }
        }),
        providesTags: ['Chart']
      }),
      getUser: build.query<GetUserResponse, GetUserArgs>({
        query: (queryArg) => ({
          url: `/api/users/${queryArg.id}`,
          params: {
            skipEnhanceUserData: queryArg.skipEnhanceUserData
          }
        }),
        providesTags: ['Users']
      }),
      getUserTagMappings: build.query<GetUserTagMappingsResponse, GetUserTagMappingsArgs>({
        query: (queryArg) => ({
          url: `/api/users/tags`,
          params: {
            ocppTags: queryArg.ocppTags
          }
        }),
        providesTags: ['Users']
      }),
      getDropdownOptions: build.query<GetDropdownOptionsResponse, GetDropdownOptionsArgs>({
        query: () => ({ url: `/api/ui/options` }),
        providesTags: ['Users']
      }),
      getTripNotes: build.query<GetTripNotesResponse, GetTripNotesArgs>({
        query: (queryArg) => ({ url: `/api/trips/${queryArg.id}/notes` }),
        providesTags: ['Trips']
      }),
      getTripReport: build.query<GetTripReportResponse, GetTripReportArgs>({
        query: (queryArg) => ({
          url: `/api/trips/report`,
          params: {
            from: queryArg['from'],
            to: queryArg.to,
            organizationId: queryArg.organizationId,
            fleetId: queryArg.fleetId,
            siteId: queryArg.siteId,
            includeDeleted: queryArg.includeDeleted
          }
        }),
        providesTags: ['Trips']
      }),
      getStatusOfGeneratedReport: build.query<GetStatusOfGeneratedReportResponse, GetStatusOfGeneratedReportArgs>({
        query: (queryArg) => ({ url: `/api/report/${queryArg.id}` }),
        providesTags: ['Notifications']
      }),
      getOrganization: build.query<GetOrganizationResponse, GetOrganizationArgs>({
        query: (queryArg) => ({ url: `/api/organizations/${queryArg.id}` }),
        providesTags: ['Organizations']
      }),
      getVehiclesForOrganization: build.query<GetVehiclesForOrganizationResponse, GetVehiclesForOrganizationArgs>({
        query: (queryArg) => ({
          url: `/api/organizations/${queryArg.id}/vehicles`,
          params: {
            includeDeleted: queryArg.includeDeleted
          }
        }),
        providesTags: ['Organizations', 'Vehicles']
      }),
      getOrganizationUsers: build.query<GetOrganizationUsersResponse, GetOrganizationUsersArgs>({
        query: (queryArg) => ({
          url: `/api/organizations/${queryArg.id}/users`,
          params: {
            skipEnhanceData: queryArg.skipEnhanceData
          }
        }),
        providesTags: ['Organizations']
      }),
      getTrips1: build.query<GetTrips1Response, GetTrips1Args>({
        query: (queryArg) => ({
          url: `/api/organizations/${queryArg.id}/trips`,
          params: {
            from: queryArg['from'],
            to: queryArg.to,
            vehicleIds: queryArg.vehicleIds,
            source: queryArg.source
          }
        }),
        providesTags: ['Organizations']
      }),
      getOrganizationTerms: build.query<GetOrganizationTermsResponse, GetOrganizationTermsArgs>({
        query: (queryArg) => ({ url: `/api/organizations/${queryArg.id}/terms` }),
        providesTags: ['Organizations']
      }),
      loginToStripe: build.query<LoginToStripeResponse, LoginToStripeArgs>({
        query: (queryArg) => ({ url: `/api/organizations/${queryArg.id}/stripe/login` }),
        providesTags: ['Organizations']
      }),
      getStripeIntentDetail: build.query<GetStripeIntentDetailResponse, GetStripeIntentDetailArgs>({
        query: (queryArg) => ({ url: `/api/organizations/${queryArg.id}/secret` }),
        providesTags: ['Organizations']
      }),
      getRoutesForOrganization: build.query<GetRoutesForOrganizationResponse, GetRoutesForOrganizationArgs>({
        query: (queryArg) => ({ url: `/api/organizations/${queryArg.id}/routes` }),
        providesTags: ['Organizations']
      }),
      getIntegrationsByOrganizationIdAndIntegrationType: build.query<
        GetIntegrationsByOrganizationIdAndIntegrationTypeResponse,
        GetIntegrationsByOrganizationIdAndIntegrationTypeArgs
      >({
        query: (queryArg) => ({ url: `/api/organizations/${queryArg.id}/integrations/${queryArg.integrationId}` }),
        providesTags: ['Organizations']
      }),
      getAllOcppTags: build.query<GetAllOcppTagsResponse, GetAllOcppTagsArgs>({
        query: (queryArg) => ({
          url: `/api/organizations/${queryArg.id}/id-tags`,
          params: {
            includeDeleted: queryArg.includeDeleted
          }
        }),
        providesTags: ['Organizations']
      }),
      getOrganizationHierarchy: build.query<GetOrganizationHierarchyResponse, GetOrganizationHierarchyArgs>({
        query: (queryArg) => ({ url: `/api/organizations/${queryArg.id}/hierarchy` }),
        providesTags: ['Organizations']
      }),
      getFleets1: build.query<GetFleets1Response, GetFleets1Args>({
        query: (queryArg) => ({ url: `/api/organizations/${queryArg.id}/fleets` }),
        providesTags: ['Organizations']
      }),
      getAllOrganizationCustomers: build.query<GetAllOrganizationCustomersResponse, GetAllOrganizationCustomersArgs>({
        query: (queryArg) => ({
          url: `/api/organizations/${queryArg.id}/customers/all`,
          params: {
            depth: queryArg.depth
          }
        }),
        providesTags: ['Organizations']
      }),
      getOrganizationConfig: build.query<GetOrganizationConfigResponse, GetOrganizationConfigArgs>({
        query: (queryArg) => ({ url: `/api/organizations/${queryArg.id}/config` }),
        providesTags: ['Organizations']
      }),
      getOrganizationApiKeyByApiKey: build.query<
        GetOrganizationApiKeyByApiKeyResponse,
        GetOrganizationApiKeyByApiKeyArgs
      >({
        query: (queryArg) => ({ url: `/api/organizations/key/${queryArg.id}` }),
        providesTags: ['Organizations']
      }),
      getCurrentOrganization: build.query<GetCurrentOrganizationResponse, GetCurrentOrganizationArgs>({
        query: () => ({ url: `/api/organizations/current` }),
        providesTags: ['Organizations']
      }),
      getLocationOfInterest: build.query<GetLocationOfInterestResponse, GetLocationOfInterestArgs>({
        query: (queryArg) => ({ url: `/api/locations/${queryArg.id}` }),
        providesTags: ['Location of Interest']
      }),
      deleteLocationOfInterest: build.mutation<DeleteLocationOfInterestResponse, DeleteLocationOfInterestArgs>({
        query: (queryArg) => ({ url: `/api/locations/${queryArg.id}`, method: 'DELETE' }),
        invalidatesTags: ['Location of Interest']
      }),
      getLocales: build.query<GetLocalesResponse, GetLocalesArgs>({
        query: () => ({ url: `/api/locales` }),
        providesTags: ['Locales']
      }),
      getFleet: build.query<GetFleetResponse, GetFleetArgs>({
        query: (queryArg) => ({ url: `/api/fleets/${queryArg.fleetId}` }),
        providesTags: ['Fleets']
      }),
      deleteFleet: build.mutation<DeleteFleetResponse, DeleteFleetArgs>({
        query: (queryArg) => ({ url: `/api/fleets/${queryArg.fleetId}`, method: 'DELETE' }),
        invalidatesTags: ['Fleets']
      }),
      getCustomers: build.query<GetCustomersResponse, GetCustomersArgs>({
        query: (queryArg) => ({
          url: `/api/customers`,
          params: {
            organizationId: queryArg.organizationId,
            page: queryArg.page,
            count: queryArg.count,
            depth: queryArg.depth
          }
        }),
        providesTags: ['Customers']
      }),
      getChartValues: build.query<GetChartValuesResponse, GetChartValuesArgs>({
        query: (queryArg) => ({
          url: `/api/chart/${queryArg.id}`,
          params: {
            from: queryArg['from'],
            to: queryArg.to,
            attributes: queryArg.attributes
          }
        }),
        providesTags: ['Chart']
      }),
      bulkResetMfa: build.mutation<BulkResetMfaResponse, BulkResetMfaArgs>({
        query: (queryArg) => ({
          url: `/api/users/reset-mfa/bulk`,
          method: 'DELETE',
          body: queryArg.bulkMfaResetRequest
        }),
        invalidatesTags: ['Users']
      }),
      deleteChargePort: build.mutation<DeleteChargePortResponse, DeleteChargePortArgs>({
        query: (queryArg) => ({ url: `/api/charge-ports/${queryArg.id}`, method: 'DELETE' }),
        invalidatesTags: ['Vehicles']
      })
    }),
    overrideExisting: false
  })
export { injectedRtkApi as enhancedApi }
export type GetVehiclesResponse = /** status 200 OK */ VehicleModel[]
export type GetVehiclesArgs = {
  ocppTags?: string[]
  activeOcppTags?: string[]
  /** Include logically (soft) deleted vehicles in the results */
  includeDeleted?: boolean
}
export type UpdateVehicleResponse = /** status 200 OK */ VehicleModel
export type UpdateVehicleArgs = {
  editVehicleModel: EditVehicleModel
}
export type CreateVehicleWithMetadataResponse = /** status 200 OK */ VehicleModel
export type CreateVehicleWithMetadataArgs = {
  createVehicleModel: CreateVehicleModel
}
export type ListWebhookSubscriptionsResponse = /** status 200 OK */ PagedResponseV2WebhookSubscriptionModel
export type ListWebhookSubscriptionsArgs = {
  page?: number
  count?: number
  sort?: string
  enabled?: boolean
}
export type UpdateWebhookSubscriptionsResponse = /** status 200 OK */ WebhookSubscriptionModel
export type UpdateWebhookSubscriptionsArgs = {
  webhookSubscriptionModel: WebhookSubscriptionModel
}
export type CreateWebhookSubscriptionsResponse = /** status 200 OK */ CreateWebhookSubscriptionResponseModel
export type CreateWebhookSubscriptionsArgs = {
  baseWebhookSubscriptionModel: BaseWebhookSubscriptionModel
}
export type GetUserGroupResponse = /** status 200 Successfully retrieved user group */ UserGroupDto
export type GetUserGroupArgs = {
  /** User group ID */
  id: string
}
export type UpdateUserGroupResponse = /** status 200 User group updated successfully */ UserGroupDto
export type UpdateUserGroupArgs = {
  /** User group ID */
  id: string
  userGroupUpdateParams: UserGroupUpdateParams
}
export type DeleteUserGroupResponse = unknown
export type DeleteUserGroupArgs = {
  /** User group ID */
  id: string
}
export type UpdateMappingResponse = /** status 200 OK */ Auth0ConnectionOrgMappingModel
export type UpdateMappingArgs = {
  auth0ConnectionId: string
  updateAuth0ConnectionOrgMappingRequest: UpdateAuth0ConnectionOrgMappingRequest
}
export type UpdateUserResponse = /** status 200 OK */ UserModel
export type UpdateUserArgs = {
  editUserModel: EditUserModel
}
export type CreateUserResponse = /** status 200 OK */ UserModel
export type CreateUserArgs = {
  isFederated?: boolean
  createUserModel: CreateUserModel
}
export type SignOutUserResponse = /** status 200 OK */ boolean
export type SignOutUserArgs = {
  id: string
}
export type ResendResponse = /** status 200 OK */ boolean
export type ResendArgs = {
  id: string
}
export type ResetPasswordResponse = /** status 200 OK */ boolean
export type ResetPasswordArgs = {
  id: string
}
export type MoveUserResponse = /** status 200 OK */ UserModel
export type MoveUserArgs = {
  id: string
  moveUserModel: MoveUserModel
}
export type EnableUserResponse = /** status 200 OK */ boolean
export type EnableUserArgs = {
  id: string
}
export type DisableUserResponse = /** status 200 OK */ boolean
export type DisableUserArgs = {
  id: string
}
export type UpdateScheduledRouteResponse = /** status 200 OK */ ScheduledRoute
export type UpdateScheduledRouteArgs = {
  scheduledRouteId: string
  scheduledRouteUpdateModel: ScheduledRouteUpdateModel
}
export type CancelScheduledRouteResponse = /** status 200 OK */ boolean
export type CancelScheduledRouteArgs = {
  scheduledRouteId: string
}
export type UpdateOrganizationResponse = /** status 200 OK */ OrganizationModel
export type UpdateOrganizationArgs = {
  editOrganizationModel: EditOrganizationModel
}
export type UpdateFleetTermsResponse = /** status 200 OK */ OrganizationTerms
export type UpdateFleetTermsArgs = {
  organizationTerms: OrganizationTerms
}
export type SaveFleetTermsResponse = /** status 200 OK */ OrganizationTerms
export type SaveFleetTermsArgs = {
  organizationTerms: OrganizationTerms
}
export type GetLocationsOfInterestForOrganizationResponse = /** status 200 OK */ LocationOfInterestResponseModel[]
export type GetLocationsOfInterestForOrganizationArgs = {
  organizationId: string
}
export type UpdateLocationOfInterestResponse = unknown
export type UpdateLocationOfInterestArgs = {
  locationOfInterestUpdateModel: LocationOfInterestUpdateModel
}
export type CreateLocationOfInterestResponse = /** status 200 OK */ LocationOfInterestResponseModel
export type CreateLocationOfInterestArgs = {
  locationOfInterestCreateModel: LocationOfInterestCreateModel
}
export type GetFleetsResponse = /** status 200 OK */ PagedResponseV2FleetModel
export type GetFleetsArgs = {
  page: number
  count: number
  sort?: string
  organizationId: string
}
export type UpdateFleetResponse = /** status 200 OK */ FleetModel
export type UpdateFleetArgs = {
  fleetModel: FleetModel
}
export type CreateFleetResponse = /** status 200 OK */ FleetModel
export type CreateFleetArgs = {
  createFleetModel: CreateFleetModel
}
export type AddVehicleEventResponse = /** status 200 OK */ VehicleEvent
export type AddVehicleEventArgs = {
  vehicleEvent: VehicleEvent
}
export type UpdateVehicleTelemetryResponse = unknown
export type UpdateVehicleTelemetryArgs = {
  vehicleTelemetryCreateParams: VehicleTelemetryCreateParams
}
export type ValidateTelematicsCredentialsResponse = /** status 200 OK */ TelematicsCredentialValidation
export type ValidateTelematicsCredentialsArgs = {
  telematicsCredentialValidationRequest: TelematicsCredentialValidationRequest
}
export type GetUserGroupsResponse = /** status 200 Successfully retrieved user groups */ UserGroupDto[]
export type GetUserGroupsArgs = {
  /** Optional organization ID to filter groups */
  organizationId?: string
}
export type CreateUserGroupResponse = /** status 201 User group created successfully */ UserGroupDto
export type CreateUserGroupArgs = {
  userGroupCreateParams: UserGroupCreateParams
}
export type BackfillTicketPrefixesResponse = /** status 200 OK */ TicketPrefixBackfillResult
export type BackfillTicketPrefixesArgs = {
  /** Zero-based page of live organizations to process */
  page?: number
  /** Organizations per page, at most 5000 */
  count?: number
}
export type GetStagedVehiclesResponse = /** status 200 OK */ PagedResponseV2StagedVehicleModel
export type GetStagedVehiclesArgs = {
  /** Integration IDs to list staged vehicles for */
  integrationIds: string[]
  page?: number
  count?: number
  sort?: string
}
export type StageVehiclesResponse = /** status 200 OK */ StagedVehicle[]
export type StageVehiclesArgs = {
  /** Integration ID to stage vehicles from */
  integrationId: string
}
export type RevokeSharingResponse = /** status 200 OK */ SharingResponseModel
export type RevokeSharingArgs = {
  id: string
  sharingId: string
}
export type RejectOfferResponse = /** status 200 OK */ SharingResponseModel
export type RejectOfferArgs = {
  id: string
  sharingId: string
}
export type AcceptOfferResponse = /** status 200 OK */ SharingResponseModel
export type AcceptOfferArgs = {
  id: string
  sharingId: string
}
export type RevokeInviteCodeResponse = /** status 200 OK */ InviteCodeResponseModel
export type RevokeInviteCodeArgs = {
  id: string
  inviteCodeId: string
}
export type OfferOrgsResponse = /** status 200 OK */ SharingResponseModel[]
export type OfferOrgsArgs = {
  id: string
  inviteCodeId: string
  offerOrgsRequestModel: OfferOrgsRequestModel
}
export type RedeemInviteCodeResponse = /** status 200 OK */ InviteCodeResponseModel
export type RedeemInviteCodeArgs = {
  id: string
  redeemCodeRequestModel: RedeemCodeRequestModel
}
export type GenerateInviteCodeResponse = /** status 200 OK */ InviteCodeResponseModel
export type GenerateInviteCodeArgs = {
  id: string
}
export type SaveOrganizationLogoVariantResponse = /** status 200 OK */ OrganizationModel
export type SaveOrganizationLogoVariantArgs = {
  id: string
  /** Theme variant of the asset: light or dark */
  variant: string
  body: {
    logo: Blob
  }
}
export type DeleteOrganizationLogoVariantResponse = /** status 200 OK */ OrganizationModel
export type DeleteOrganizationLogoVariantArgs = {
  id: string
  /** Theme variant of the asset: light or dark */
  variant: string
}
export type SaveOrganizationIconVariantResponse = /** status 200 OK */ OrganizationModel
export type SaveOrganizationIconVariantArgs = {
  id: string
  /** Theme variant of the asset: light or dark */
  variant: string
  body: {
    icon: Blob
  }
}
export type DeleteOrganizationIconVariantResponse = /** status 200 OK */ OrganizationModel
export type DeleteOrganizationIconVariantArgs = {
  id: string
  /** Theme variant of the asset: light or dark */
  variant: string
}
export type OnboardVehiclesResponse = /** status 200 OK */ StagedVehicle[]
export type OnboardVehiclesArgs = {
  onboardVehiclesRequest: OnboardVehiclesRequest
}
export type CreateMappingResponse = /** status 200 OK */ Auth0ConnectionOrgMappingModel
export type CreateMappingArgs = {
  createAuth0ConnectionOrgMappingRequest: CreateAuth0ConnectionOrgMappingRequest
}
export type InitiateMfaSetupResponse = /** status 200 OK */ MfaModel
export type InitiateMfaSetupArgs = void
export type VerifySoftwareTokenResponse = unknown
export type VerifySoftwareTokenArgs = {
  userCode: string
}
export type LookupByAuthProviderResponse = /** status 200 OK */ AuthProviderLookupResponse
export type LookupByAuthProviderArgs = {
  authProviderLookupRequest: AuthProviderLookupRequest
}
export type GetOrCreateUserIdentityResponse = /** status 200 OK */ UserIdentityResponse
export type GetOrCreateUserIdentityArgs = {
  userIdentityRequest: UserIdentityRequest
}
export type CreateSimTripResponse = /** status 200 OK */ Trip
export type CreateSimTripArgs = {
  simTripCreateParams: SimTripCreateParams
}
export type CreateTripNoteResponse = /** status 200 OK */ TripNote
export type CreateTripNoteArgs = {
  tripNote: TripNote
}
export type GetScheduledRoutesResponse = /** status 200 OK */ PagedResponseV1ScheduledRouteModel
export type GetScheduledRoutesArgs = {
  vehicleId?: string
  siteId?: string
  ocppTag?: string
  scheduledRouteType?: 'USER_CREATED' | 'PREDICTED' | 'GEOTAB' | 'ZONAR' | 'SAMSARA' | 'OTHER'
  departFrom?: string
  departTo?: string
  arriveFrom?: string
  arriveTo?: string
  page?: number
  size?: number
}
export type CreateScheduledRouteResponse = /** status 200 OK */ ScheduleRouteResponse
export type CreateScheduledRouteArgs = {
  scheduleRouteRequest: ScheduleRouteRequest
}
export type AssignVehicleResponse = /** status 200 OK */ VehicleModel
export type AssignVehicleArgs = {
  id: string
  vehicleId: string
}
export type OnboardOrganizationToStripeResponse = /** status 200 OK */ StringResponseModel
export type OnboardOrganizationToStripeArgs = {
  id: string
  urlRedirectModel: UrlRedirectModel
}
export type GetSsoConnectionDetailsResponse = /** status 200 OK */ SsoConfigResponse
export type GetSsoConnectionDetailsArgs = {
  id: string
}
export type CreateSsoConfigurationTicketResponse = /** status 200 OK */ Auth0SsoTicketResponse
export type CreateSsoConfigurationTicketArgs = {
  id: string
  allowedDomains?: string[]
  idpInitiatedEnabled?: boolean
  displayName?: string
}
export type DeleteSsoConfigurationResponse = /** status 200 OK */ OrganizationModel
export type DeleteSsoConfigurationArgs = {
  id: string
}
export type TransferSsoConfigurationResponse = /** status 200 OK */ OrganizationModel
export type TransferSsoConfigurationArgs = {
  id: string
  transferSsoConfigRequest: TransferSsoConfigRequest
}
export type SaveOrganizationLogoResponse = /** status 200 OK */ OrganizationModel
export type SaveOrganizationLogoArgs = {
  id: string
  body: {
    logo: Blob
  }
}
export type DeleteOrganizationLogoResponse = /** status 200 OK */ OrganizationModel
export type DeleteOrganizationLogoArgs = {
  id: string
}
export type GetApiKeysByOrganizationIdResponse = /** status 200 OK */ OrganizationApiKeyModel[]
export type GetApiKeysByOrganizationIdArgs = {
  id: string
}
export type ProvisionApiKeyByOrganizationIdResponse = /** status 200 OK */ OrganizationApiKeyModel
export type ProvisionApiKeyByOrganizationIdArgs = {
  id: string
  organizationApiKeyCreateParams: OrganizationApiKeyCreateParams
}
export type GetIntegrationsByOrganizationIdResponse = /** status 200 OK */ OrganizationIntegration[]
export type GetIntegrationsByOrganizationIdArgs = {
  id: string
}
export type SaveOrganizationIntegrationResponse = /** status 200 OK */ OrganizationIntegration
export type SaveOrganizationIntegrationArgs = {
  id: string
  organizationIntegration: OrganizationIntegration
}
export type SaveOrganizationIconResponse = /** status 200 OK */ OrganizationModel
export type SaveOrganizationIconArgs = {
  id: string
  body: {
    icon: Blob
  }
}
export type DeleteOrganizationIconResponse = /** status 200 OK */ OrganizationModel
export type DeleteOrganizationIconArgs = {
  id: string
}
export type GetOrganizationCustomersResponse = /** status 200 OK */ OrganizationModel[]
export type GetOrganizationCustomersArgs = {
  id: string
  skipEnhanceData?: boolean
}
export type CreateOrganizationResponse = /** status 200 OK */ OrganizationModel
export type CreateOrganizationArgs = {
  id: string
  createOrganizationModel: CreateOrganizationModel
}
export type PublishToSnsResponse = unknown
export type PublishToSnsArgs = {
  subEntity: 'TELEMETRY' | 'BASE' | 'INVALID'
  event: 'NEW' | 'UPDATED' | 'DELETED' | 'TRIP_SUMMARY' | 'COMPLETED_ROUTE_SUMMARY' | 'CHARGING_SUMMARY' | 'INVALID'
  body: object
}
export type CreateChargePortResponse = /** status 200 OK */ ChargePortModel
export type CreateChargePortArgs = {
  createChargePortModel: CreateChargePortModel
}
export type AssignVehicleToBillingOrganizationResponse = /** status 200 OK */ VehicleModel
export type AssignVehicleToBillingOrganizationArgs = {
  id: string
  vehicleId: string
}
export type PatchOrganizationFeaturesResponse = /** status 200 OK */ PatchOrganizationFeaturesResult
export type PatchOrganizationFeaturesArgs = {
  id: string
  patchOrganizationFeaturesModel: PatchOrganizationFeaturesModel
}
export type GetVehicleResponse = /** status 200 OK */ VehicleModel
export type GetVehicleArgs = {
  id: string
  /** Return the vehicle even if it has been logically (soft) deleted */
  includeDeleted?: boolean
}
export type GetLatestVehicleWeatherForecastResponse = /** status 200 OK */ VehicleWeatherForecast
export type GetLatestVehicleWeatherForecastArgs = {
  id: string
  /** Include the vehicle even if it has been logically (soft) deleted */
  includeDeleted?: boolean
}
export type GetTripsResponse = /** status 200 OK */ Trip[]
export type GetTripsArgs = {
  id: string
  from?: string
  to?: string
  source?: string
}
export type GetTripResponse = /** status 200 OK */ Trip
export type GetTripArgs = {
  id: string
  tripId: string
}
export type GetVehicleTelematicsResponse = /** status 200 OK */ AssetTelematics[]
export type GetVehicleTelematicsArgs = {
  id: string
  from?: string
  to?: string
}
export type GetServiceableRangeForVehicleResponse = /** status 200 OK */ AzureServiceableRangeResponse
export type GetServiceableRangeForVehicleArgs = {
  id: string
  /** Include the vehicle even if it has been logically (soft) deleted */
  includeDeleted?: boolean
}
export type GetMetricsForVehicleResponse = /** status 200 OK */ VehicleMetricsModel
export type GetMetricsForVehicleArgs = {
  id: string
  date: string
  /** Include the vehicle even if it has been logically (soft) deleted */
  includeDeleted?: boolean
}
export type GetVehicleFaultsResponse = /** status 200 OK */ PagedResponseV1VehicleFaultModel
export type GetVehicleFaultsArgs = {
  id: string
  page: number
  count: number
  sort?: string
  searchString?: string
  from?: string
  to?: string
}
export type GetAssetEventsResponse = /** status 200 OK */ VehicleEvent[]
export type GetAssetEventsArgs = {
  id: string
  type?: string
  from?: string
  to?: string
}
export type GetVehicleStatusBreakdownForVehiclesResponse = /** status 200 OK */ TimeUtilization
export type GetVehicleStatusBreakdownForVehiclesArgs = {
  from: string
  to?: string
  organizationId?: string
  fleetId?: string
  /** Include logically (soft) deleted vehicles in the results */
  includeDeleted?: boolean
}
export type GetTimespanDataForVehiclesResponse = /** status 200 OK */ VehicleMetrics[]
export type GetTimespanDataForVehiclesArgs = {
  from: string
  to?: string
  organizationId?: string
  fleetId?: string
  /** Include logically (soft) deleted vehicles in the results */
  includeDeleted?: boolean
}
export type GetVehiclesBySiteResponse = /** status 200 OK */ VehicleModel[]
export type GetVehiclesBySiteArgs = {
  id: string
  /** Include logically (soft) deleted vehicles in the results */
  includeDeleted?: boolean
}
export type GetVehicleReportResponse = /** status 200 OK */ ReportGenerationStatus
export type GetVehicleReportArgs = {
  from: string
  to?: string
  organizationId?: string
  fleetId?: string
  siteId?: string[]
  /** Include logically (soft) deleted vehicles in the report */
  includeDeleted?: boolean
}
export type GetLatestVehicleStatusesResponse = /** status 200 OK */ LatestVehicleStatusSummaryModel
export type GetLatestVehicleStatusesArgs = {
  siteId: string
  /** Include logically (soft) deleted vehicles in the status counts */
  includeDeleted?: boolean
}
export type GetMetricsForVehiclesResponse = /** status 200 OK */ VehicleMetricsModel
export type GetMetricsForVehiclesArgs = {
  from: string
  to?: string
  organizationId?: string
  fleetId?: string
  /** Include logically (soft) deleted vehicles in the results */
  includeDeleted?: boolean
}
export type GetVehicleAuthTagsResponse = /** status 200 OK */ PagedResponseV2VehicleAuthTagModel
export type GetVehicleAuthTagsArgs = {
  vehicleIds: string[]
  page: number
  count: number
  sort?: string
  from?: string
  to?: string
}
export type ListWebhookSubscriptions1Response = /** status 200 OK */ PagedResponseV2WebhookSubscriptionV2Model
export type ListWebhookSubscriptions1Args = {
  page?: number
  count?: number
  sort?: string
  enabled?: boolean
}
export type GetWebhookSubscriptionResponse = /** status 200 OK */ WebhookSubscriptionV2Model
export type GetWebhookSubscriptionArgs = {
  id: string
}
export type GetWebhookSubscription1Response = /** status 200 OK */ WebhookSubscriptionModel
export type GetWebhookSubscription1Args = {
  id: string
}
export type DeleteWebhookSubscriptionResponse = unknown
export type DeleteWebhookSubscriptionArgs = {
  id: string
}
export type GetVehicleFaults1Response = /** status 200 OK */ PagedResponseV2VehicleFaultModel
export type GetVehicleFaults1Args = {
  /** Vehicle ID */
  vehicleId: string
  /** Page number */
  page?: number
  /** Number of items per page */
  count?: number
  /** Field to sort items by, options are timestamp, provider, occurrenceCount, sourceName, sourceAddress, spn, spnName, fmi */
  sort?: string
  /** Start date/time to filter faults */
  from?: string
  /** End date/time to filter faults */
  to?: string
}
export type GetUsersResponse = /** status 200 OK */ PagedResponseV2UserSummaryModel
export type GetUsersArgs = {
  /** Page number */
  page?: number
  /** Number of items per page */
  count?: number
  /** Field to sort by, options are name, email. Prefix with '-' for descending order (e.g. sort=name for ascending, sort=-name for descending). */
  sort?: string
  /** How many organization levels below the current organization to include */
  depth?: number
}
export type GetDescartesRouteByIdResponse = /** status 200 OK */ RouteDetailModel
export type GetDescartesRouteByIdArgs = {
  id: string
}
export type GetVehiclesForOrganizationV1Response = /** status 200 OK */ PagedResponseV2VehicleModel
export type GetVehiclesForOrganizationV1Args = {
  id: string
  page?: number
  count?: number
  sort?: string
  ocppTags?: string[]
  siteIds?: string[]
  /** Include logically (soft) deleted vehicles in the results */
  includeDeleted?: boolean
}
export type GetActiveCodesResponse = /** status 200 OK */ InviteCodeResponseModel[]
export type GetActiveCodesArgs = {
  id: string
}
export type GetGrantorSharesResponse = /** status 200 OK */ SharingResponseModel[]
export type GetGrantorSharesArgs = {
  id: string
}
export type GetGrantorPendingResponse = /** status 200 OK */ SharingResponseModel[]
export type GetGrantorPendingArgs = {
  id: string
}
export type GetGranteeSharesResponse = /** status 200 OK */ SharingResponseModel[]
export type GetGranteeSharesArgs = {
  id: string
}
export type GetSharingAgreementsResponse = /** status 200 OK */ SharingAgreementModel[]
export type GetSharingAgreementsArgs = {
  id: string
}
export type GetChartValuesV1Response = /** status 200 OK */ {
  [key: string]: ChartSeriesV1Model
}
export type GetChartValuesV1Args = {
  id: string
  /** Defaults to last 14 days if from is not supplied. */
  from?: string
  /** Defaults to current time if to is not supplied. */
  to?: string
  attributes: string[]
}
export type GetUserResponse = /** status 200 OK */ UserModel
export type GetUserArgs = {
  id: string
  skipEnhanceUserData?: boolean
}
export type GetUserTagMappingsResponse = /** status 200 OK */ {
  [key: string]: string
}
export type GetUserTagMappingsArgs = {
  ocppTags?: string[]
}
export type GetDropdownOptionsResponse = /** status 200 OK */ OptionValue[]
export type GetDropdownOptionsArgs = void
export type GetTripNotesResponse = /** status 200 OK */ TripNote[]
export type GetTripNotesArgs = {
  id: string
}
export type GetTripReportResponse = /** status 200 OK */ ReportGenerationStatus
export type GetTripReportArgs = {
  from: string
  to?: string
  organizationId?: string
  fleetId?: string
  siteId?: string[]
  /** Include logically (soft) deleted vehicles in the report */
  includeDeleted?: boolean
}
export type GetStatusOfGeneratedReportResponse = /** status 200 OK */ ReportGenerationStatus
export type GetStatusOfGeneratedReportArgs = {
  id: string
}
export type GetOrganizationResponse = /** status 200 OK */ OrganizationModel
export type GetOrganizationArgs = {
  id: string
}
export type GetVehiclesForOrganizationResponse = /** status 200 OK */ VehicleModel[]
export type GetVehiclesForOrganizationArgs = {
  id: string
  /** Include logically (soft) deleted vehicles in the results */
  includeDeleted?: boolean
}
export type GetOrganizationUsersResponse = /** status 200 OK */ UserModel[]
export type GetOrganizationUsersArgs = {
  id: string
  skipEnhanceData?: boolean
}
export type GetTrips1Response = /** status 200 OK */ Trip[]
export type GetTrips1Args = {
  id: string
  from?: string
  to?: string
  vehicleIds?: string[]
  source?: string
}
export type GetOrganizationTermsResponse = /** status 200 OK */ OrganizationTerms[]
export type GetOrganizationTermsArgs = {
  id: string
}
export type LoginToStripeResponse = /** status 200 OK */ StringResponseModel
export type LoginToStripeArgs = {
  id: string
}
export type GetStripeIntentDetailResponse = /** status 200 OK */ StripeIntentModel
export type GetStripeIntentDetailArgs = {
  id: string
}
export type GetRoutesForOrganizationResponse = /** status 200 OK */ Route[]
export type GetRoutesForOrganizationArgs = {
  id: string
}
export type GetIntegrationsByOrganizationIdAndIntegrationTypeResponse = /** status 200 OK */ OrganizationIntegration[]
export type GetIntegrationsByOrganizationIdAndIntegrationTypeArgs = {
  id: string
  integrationId: string
}
export type GetAllOcppTagsResponse = /** status 200 OK */ OcppTag[]
export type GetAllOcppTagsArgs = {
  id: string
  /** Include OCPP tags of logically (soft) deleted vehicles in the results */
  includeDeleted?: boolean
}
export type GetOrganizationHierarchyResponse = /** status 200 OK */ string[]
export type GetOrganizationHierarchyArgs = {
  id: string
}
export type GetFleets1Response = /** status 200 OK */ Fleet[]
export type GetFleets1Args = {
  id: string
}
export type GetAllOrganizationCustomersResponse = /** status 200 OK */ string[]
export type GetAllOrganizationCustomersArgs = {
  id: string
  depth?: number
}
export type GetOrganizationConfigResponse = /** status 200 OK */ OrganizationConfig[]
export type GetOrganizationConfigArgs = {
  id: string
}
export type GetOrganizationApiKeyByApiKeyResponse = /** status 200 OK */ OrganizationApiKeyModel
export type GetOrganizationApiKeyByApiKeyArgs = {
  id: string
}
export type GetCurrentOrganizationResponse = /** status 200 OK */ ExternalOrganizationModel
export type GetCurrentOrganizationArgs = void
export type GetLocationOfInterestResponse = /** status 200 OK */ LocationOfInterestResponseModel
export type GetLocationOfInterestArgs = {
  id: string
}
export type DeleteLocationOfInterestResponse = unknown
export type DeleteLocationOfInterestArgs = {
  id: string
}
export type GetLocalesResponse = /** status 200 OK */ LocaleModel[]
export type GetLocalesArgs = void
export type GetFleetResponse = /** status 200 OK */ FleetModel
export type GetFleetArgs = {
  fleetId: string
}
export type DeleteFleetResponse = unknown
export type DeleteFleetArgs = {
  fleetId: string
}
export type GetCustomersResponse = /** status 200 OK */ PagedResponseV2Customer
export type GetCustomersArgs = {
  organizationId: string
  page?: number
  count?: number
  depth?: number
}
export type GetChartValuesResponse = /** status 200 OK */ ChartDataResponseModel
export type GetChartValuesArgs = {
  id: string
  /** Defaults to last 14 days if from are not supplied. */
  from?: string
  /** Defaults to current time if to is not supplied. */
  to?: string
  attributes: string[]
}
export type BulkResetMfaResponse = /** status 200 OK */ BulkOperationResponseMfaResetResponseData
export type BulkResetMfaArgs = {
  bulkMfaResetRequest: BulkMfaResetRequest
}
export type DeleteChargePortResponse = unknown
export type DeleteChargePortArgs = {
  id: string
}
export type SourceAttributeValueBigDecimal = {
  units?: string
  lastUpdated?: number
  source?: string
  value?: number
}
export type LocationOfInterestModel = {
  id?: string
  type?: string
}
export type SourceAttributeValueString = {
  units?: string
  lastUpdated?: number
  source?: string
  value?: string
}
export type CurrentLocation = {
  locationOfInterestModels?: LocationOfInterestModel[]
  bearing?: SourceAttributeValueBigDecimal
  cardinalDirection?: SourceAttributeValueString
  street?: SourceAttributeValueString
}
export type Subscription = {
  isSubscriptionActive?: boolean
  subscriptionType?: string
  subscriptionTerm?: string
  startDate?: string
  endDate?: string
  autoRenewal?: boolean
}
export type Fault = {
  code?: string
  name?: string
  description?: string
  latitude?: number
  longitude?: number
  sourceAddress?: string
  parameterIdentifier?: string
  failureModeIndicator?: string
  occurrenceCount?: number
  lastUpdated?: number
  provider?: string
  faultType?: number
  mil?: string[]
}
export type ChargePortModel = {
  /** Human-readable name for the charge port (e.g. "Front port", "Left inlet"). Used to disambiguate ports on multi-port vehicles. */
  name?: string
  /** Charge port / connector type; used for reporting and display. Optional -- omit it if the connector standard is unknown. Values are case- and space-sensitive and must be one of the listed options. */
  type?: 'J1772' | 'Type 2' | 'CHAdeMO' | 'CCS1' | 'CCS2' | 'NACS' | 'MCS' | 'Other'
  /** OCPP auth tag (idTag) to associate with this charge port. Optional. When provided, the value is normalized before storage and must be unique across your organization's active auth tags; a duplicate returns 409 Conflict. Registering an auth tag also registers the corresponding OCPP id-tag variants so chargers will accept it on transaction start. */
  authTag?: string
  /** The id for a charge port */
  id: string
  /** The vehicle id that this charge port is mapped to */
  vehicleId: string
}
export type VehicleAuthTagModel = {
  /** The id for a charge port */
  chargePortId: string
  /** The id for a vehicle the charge port is mapped to */
  vehicleId: string
  /** The datetime that the auth tag is valid from */
  validFrom: string
  /** The datetime that the auth tag is valid to */
  validTo?: string
  /** The status of the auth tag mapped to this port */
  status: 'ACTIVE' | 'DEACTIVATED'
  /** The name for a charge port */
  name?: string
  /** The type for a charge port */
  type?: string
  /** The auth tag mapped to the charge port */
  authTag: string
  /** The normalized version of the auth tag that is mapped to the charge port */
  normalizedAuthTag: string
}
export type VehicleModel = {
  /** The vin for a vehicle */
  vin: string
  /** The name for a vehicle */
  vehicleNm: string
  /** The display name for a vehicle */
  vehicleDisplayNm?: string
  /** The organizationId for a vehicle */
  organizationId: string
  /** The make for a vehicle */
  make: string
  /** The model for a vehicle */
  model: string
  /** The model year for a vehicle */
  modelYear: number
  /** Unique identifier for the site where the vehicle is based */
  homeSiteId?: string
  /** The battery size for a vehicle in kWh */
  batterySize: number
  /** The efficiency for a vehicle, unit is set by efficiencyUnit */
  efficiency?: number
  /** The efficiency unit for a vehicle, defaults to kWh/mi */
  efficiencyUnit?: 'km/kwh' | 'kwh/km' | 'mi/kwh' | 'kwh/mi'
  /** The estimated range for a vehicle, unit is set by estimatedRangeUnit */
  estimatedRange: number
  /** The estimated range unit for a vehicle, defaults to mi */
  estimatedRangeUnit?: 'mi' | 'km'
  /** The weight for a vehicle */
  weight: SourceAttributeValueBigDecimal
  /** The external id for a vehicle */
  externalId?: string
  /** The commissioning status for a vehicle */
  vehicleCommissionStatus?: 'PRE_COMMISSIONED' | 'COMMISSIONED' | 'MAINTENANCE' | 'OUT_OF_SERVICE' | 'DECOMMISSIONED'
  /** Minimum state of charge threshold for the vehicle (0-100) */
  minSoc?: number
  /** Maximum state of charge threshold for the vehicle (0-100). When null, the vehicle inherits the site-level default. */
  maxSoc?: number
  /** Whether the vehicle has an associated simulator */
  hasSimulator?: boolean
  /** Default loading time in minutes before a trip, during which the vehicle cannot charge. A scheduled trip may override it. */
  defaultLoadingMinutes?: number
  /** Maximum charge rate for the vehicle in kW. When null, no vehicle-level cap applies. */
  maxChargeRateKw?: number
  /** Whether the vehicle is autonomous. Defaults to false. */
  isAutonomous?: boolean
  /** Catalog make-model to link the vehicle to. When omitted, make/model are matched against the catalog by name and fall back to the Uncatalogued entry. When present, make/model are set from the catalog. */
  makeModelId?: string
  /** Pin the vehicle to a specific capability-set version of its make-model. When omitted, the set is resolved from the make-model and model year. Sending back the currently resolved, unpinned set does not pin it. */
  capabilitySetId?: string
  /** Trim / configuration label, e.g. battery variant */
  variantName?: string
  /** Unique identifier for a vehicle in the Synop system */
  id: string
  /** Type of vehicle */
  vehicleType?: string
  /** Whether capabilitySetId was pinned explicitly rather than resolved from the make-model */
  capabilitySetPinned?: boolean
  /** How the vehicle was linked to its catalog make-model */
  catalogLinkSource?: 'USER' | 'PROVIDER' | 'VIN_DECODE' | 'BACKFILL' | 'ADMIN' | 'DEFAULT'
  /** The macAddress for a vehicle. This field is deprecated and will be removed on June 25, 2025. Please use the chargePorts field instead. */
  macAddress?: string
  /** Class of vehicle */
  vehicleClass?: number
  /** Whether the vehicle is active in the system or not. Inactive vehicles may not collect new telemetry */
  active?: boolean
  /** Whether the vehicle has been logically deleted from the system */
  logicalDeleteIn?: boolean
  /** Provider ID of the vehicle. This usually is defined by the upstream telematics source */
  providerId?: string
  /** Whether the vehicle is capable of vehicle-to-grid operations */
  vehicleToGridCapable?: boolean
  /** Lifetime efficiency of the vehicle */
  lifetimeEfficiency?: number
  /** Unique identifier for the fleet to which the vehicle belongs */
  fleetId?: string
  /** Name of the fleet to which the vehicle belongs */
  fleetNm?: string
  /** The name of the organization to which the vehicle belongs */
  organizationNm?: string
  /** Unique identifier for the organization that is billed for this vehicle. Check the billing module for more information */
  billingOrganizationId?: string
  /** Current location of interest the vehicle is in */
  currentLocation?: CurrentLocation
  /** Status of the vehicle as of the telematics timestamp */
  vehicleStatus?: SourceAttributeValueString
  /** Latitude of the vehicle as of the telematics timestamp */
  latitude?: SourceAttributeValueBigDecimal
  /** Longitude of the vehicle as of the telematics timestamp */
  longitude?: SourceAttributeValueBigDecimal
  /** Remaining charge time of the vehicle caculated as of the telematics timestamp */
  remainingChargeTime?: SourceAttributeValueBigDecimal
  /** Remaining range of the vehicle calculated as of the telematics timestamp */
  remainingRange?: SourceAttributeValueBigDecimal
  /** State of charge of the vehicle as of the telematics timestamp */
  stateOfCharge?: SourceAttributeValueBigDecimal
  /** Odometer reading of the vehicle as of the telematics timestamp */
  odometer?: SourceAttributeValueBigDecimal
  /** Speed of the vehicle as of the telematics timestamp */
  speed?: SourceAttributeValueBigDecimal
  /** State of the 12V system */
  batteryPotential?: SourceAttributeValueBigDecimal
  /** Distance from current location to the home site */
  distanceUntilHome?: SourceAttributeValueBigDecimal
  /** Time it would take to go from current location to the home site */
  timeUntilHome?: SourceAttributeValueBigDecimal
  /** Aggregate lifetime engine on time */
  engineOnLifetime?: SourceAttributeValueBigDecimal
  /** Aggregate lifetime driving time */
  drivingTimeLifetime?: SourceAttributeValueBigDecimal
  /** Aggregate lifetime EPTO use time */
  eptoUseLifetime?: SourceAttributeValueBigDecimal
  /** Aggregate lifetime battery discharge energy */
  batteryDischargeLifetime?: SourceAttributeValueBigDecimal
  /** Aggregate lifetime battery regen energy */
  batteryRegenLifetime?: SourceAttributeValueBigDecimal
  /** Aggregate lifetime energy used by the HVAC system */
  tracInverterMotorLifetime?: SourceAttributeValueBigDecimal
  /** Aggregate lifetime energy used by the HVAC system */
  tracInverterRegenLifetime?: SourceAttributeValueBigDecimal
  /** Aggregate lifetime energy used by the HVAC system */
  batteryManagementLifetime?: SourceAttributeValueBigDecimal
  dcdcLifetime?: SourceAttributeValueBigDecimal
  /** Aggregate lifetime energy used by the HVAC system */
  hvacLifetime?: SourceAttributeValueBigDecimal
  /** Average charger power supplied to the vehicle */
  chargerPowerSupplied?: SourceAttributeValueBigDecimal
  /** Subscriptions attached to this vehicle. Check the billing module for more information */
  subscriptions?: Subscription[]
  /** Active faults attached to this vehicle */
  faults?: Fault[]
  /** Telematics timestamp of the latest data received for the vehicle */
  telematicsTimestamp?: string
  /** Telematics timestamp of the latest data received for the vehicle in epoch milliseconds */
  telematicsTimestampEpochMillisecond?: number
  /** Ambient air temperature captured as of the vehicle as of the telematics timestamp */
  ambientAirTemperature?: SourceAttributeValueBigDecimal
  /** Whether the vehicle is in the home site or not */
  inDepot?: boolean
  /** Charge Ports mapped to this vehicle */
  chargePorts?: ChargePortModel[]
  /** A list of all auth tags associated with this vehicle (historical & current) */
  vehicleAuthTags?: VehicleAuthTagModel[]
}
export type EditVehicleModel = {
  /** The vin for a vehicle */
  vin: string
  /** The name for a vehicle */
  vehicleNm: string
  /** The display name for a vehicle */
  vehicleDisplayNm?: string
  /** The organizationId for a vehicle */
  organizationId: string
  /** The make for a vehicle */
  make: string
  /** The model for a vehicle */
  model: string
  /** The model year for a vehicle */
  modelYear: number
  /** The id of the home site for a vehicle */
  homeSiteId?: string
  /** The battery size for a vehicle in kWh */
  batterySize: number
  /** The efficiency for a vehicle, unit is set by efficiencyUnit */
  efficiency?: number
  /** The efficiency unit for a vehicle, defaults to kWh/mi */
  efficiencyUnit?: 'km/kwh' | 'kwh/km' | 'mi/kwh' | 'kwh/mi'
  /** The estimated range for a vehicle, unit is set by estimatedRangeUnit */
  estimatedRange: number
  /** The estimated range unit for a vehicle, defaults to mi */
  estimatedRangeUnit?: 'mi' | 'km'
  /** The weight for a vehicle */
  weight: SourceAttributeValueBigDecimal
  /** The external id for a vehicle */
  externalId?: string
  /** The commissioning status for a vehicle */
  vehicleCommissionStatus?: 'PRE_COMMISSIONED' | 'COMMISSIONED' | 'MAINTENANCE' | 'OUT_OF_SERVICE' | 'DECOMMISSIONED'
  /** Minimum state of charge threshold for the vehicle (0-100) */
  minSoc?: number
  /** Maximum state of charge threshold for the vehicle (0-100). When null, the vehicle inherits the site-level default. */
  maxSoc?: number
  /** Whether the vehicle has an associated simulator */
  hasSimulator?: boolean
  /** Default loading time in minutes before a trip, during which the vehicle cannot charge. A scheduled trip may override it. */
  defaultLoadingMinutes?: number
  /** Maximum charge rate for the vehicle in kW. When null, no vehicle-level cap applies. */
  maxChargeRateKw?: number
  /** Whether the vehicle is autonomous. Defaults to false. */
  isAutonomous?: boolean
  /** Catalog make-model to link the vehicle to. When omitted, make/model are matched against the catalog by name and fall back to the Uncatalogued entry. When present, make/model are set from the catalog. */
  makeModelId?: string
  /** Pin the vehicle to a specific capability-set version of its make-model. When omitted, the set is resolved from the make-model and model year. Sending back the currently resolved, unpinned set does not pin it. */
  capabilitySetId?: string
  /** Trim / configuration label, e.g. battery variant */
  variantName?: string
  /** The id for a vehicle */
  id: string
  /** Charge Ports mapped to this vehicle */
  chargePorts?: ChargePortModel[]
  /** The Fleet that this vehicle is assigned to */
  fleetId?: string
}
export type CreateChargePortModel = {
  /** Human-readable name for the charge port (e.g. "Front port", "Left inlet"). Used to disambiguate ports on multi-port vehicles. */
  name?: string
  /** Charge port / connector type; used for reporting and display. Optional -- omit it if the connector standard is unknown. Values are case- and space-sensitive and must be one of the listed options. */
  type?: 'J1772' | 'Type 2' | 'CHAdeMO' | 'CCS1' | 'CCS2' | 'NACS' | 'MCS' | 'Other'
  /** OCPP auth tag (idTag) to associate with this charge port. Optional. When provided, the value is normalized before storage and must be unique across your organization's active auth tags; a duplicate returns 409 Conflict. Registering an auth tag also registers the corresponding OCPP id-tag variants so chargers will accept it on transaction start. */
  authTag?: string
  /** Id of the existing vehicle to attach this charge port to. The caller must have write access to the vehicle's organization. */
  vehicleId: string
}
export type CreateVehicleModel = {
  /** The vin for a vehicle */
  vin: string
  /** The name for a vehicle */
  vehicleNm: string
  /** The display name for a vehicle */
  vehicleDisplayNm?: string
  /** The organizationId for a vehicle */
  organizationId: string
  /** The make for a vehicle */
  make: string
  /** The model for a vehicle */
  model: string
  /** The model year for a vehicle */
  modelYear: number
  /** The id of the home site for a vehicle */
  homeSiteId?: string
  /** The battery size for a vehicle in kWh */
  batterySize: number
  /** The efficiency for a vehicle, unit is set by efficiencyUnit */
  efficiency?: number
  /** The efficiency unit for a vehicle, defaults to kWh/mi */
  efficiencyUnit?: 'km/kwh' | 'kwh/km' | 'mi/kwh' | 'kwh/mi'
  /** The estimated range for a vehicle, unit is set by estimatedRangeUnit */
  estimatedRange: number
  /** The estimated range unit for a vehicle, defaults to mi */
  estimatedRangeUnit?: 'mi' | 'km'
  /** The weight for a vehicle */
  weight: SourceAttributeValueBigDecimal
  /** The external id for a vehicle */
  externalId?: string
  /** The commissioning status for a vehicle */
  vehicleCommissionStatus?: 'PRE_COMMISSIONED' | 'COMMISSIONED' | 'MAINTENANCE' | 'OUT_OF_SERVICE' | 'DECOMMISSIONED'
  /** Minimum state of charge threshold for the vehicle (0-100) */
  minSoc?: number
  /** Maximum state of charge threshold for the vehicle (0-100). When null, the vehicle inherits the site-level default. */
  maxSoc?: number
  /** Whether the vehicle has an associated simulator */
  hasSimulator?: boolean
  /** Default loading time in minutes before a trip, during which the vehicle cannot charge. A scheduled trip may override it. */
  defaultLoadingMinutes?: number
  /** Maximum charge rate for the vehicle in kW. When null, no vehicle-level cap applies. */
  maxChargeRateKw?: number
  /** Whether the vehicle is autonomous. Defaults to false. */
  isAutonomous?: boolean
  /** Catalog make-model to link the vehicle to. When omitted, make/model are matched against the catalog by name and fall back to the Uncatalogued entry. When present, make/model are set from the catalog. */
  makeModelId?: string
  /** Pin the vehicle to a specific capability-set version of its make-model. When omitted, the set is resolved from the make-model and model year. Sending back the currently resolved, unpinned set does not pin it. */
  capabilitySetId?: string
  /** Trim / configuration label, e.g. battery variant */
  variantName?: string
  /** Charge Ports mapped to this vehicle */
  chargePorts?: CreateChargePortModel[]
  /** The Fleet that this vehicle is assigned to */
  fleetId?: string
}
export type PaginationMetadata = {
  first: boolean
  last: boolean
  pageSize: number
  page: number
  totalPages: number
  totalItems: number
}
export type Meta = {
  pagination: PaginationMetadata
}
export type WebhookSubscriptionModel = {
  /** The organization id that the webhook subscription is created under */
  organizationId: string
  /** The name of the webhook subscription */
  name: string
  /** The endpoint for the webhook subscription for which the events will be sent to */
  endpoint: string
  events: (
    | 'charger.status_notification'
    | 'charger.boot_notification'
    | 'charger.transaction_event'
    | 'charger.data_transfer'
    | 'charger.ocpp_logs'
    | 'vehicle.telemetry_received'
    | 'vehicle.optimization'
  )[]
  /** The unique id for the webhook subscription */
  id: string
  /** The status for the webhook subscription */
  enabled: boolean
}
export type PagedResponseV2WebhookSubscriptionModel = {
  meta: Meta
  data: WebhookSubscriptionModel[]
}
export type CreateWebhookSubscriptionResponseModel = {
  /** The organization id that the webhook subscription is created under */
  organizationId: string
  /** The name of the webhook subscription */
  name: string
  /** The endpoint for the webhook subscription for which the events will be sent to */
  endpoint: string
  events: (
    | 'charger.status_notification'
    | 'charger.boot_notification'
    | 'charger.transaction_event'
    | 'charger.data_transfer'
    | 'charger.ocpp_logs'
    | 'vehicle.telemetry_received'
    | 'vehicle.optimization'
  )[]
  /** The id for the webhook subscription */
  id: string
  /** The status for the webhook subscription */
  enabled: boolean
  /** The secret for the webhook subscription */
  secret: string
}
export type BaseWebhookSubscriptionModel = {
  /** The organization id that the webhook subscription is created under */
  organizationId: string
  /** The name of the webhook subscription */
  name: string
  /** The endpoint for the webhook subscription for which the events will be sent to */
  endpoint: string
  events: (
    | 'charger.status_notification'
    | 'charger.boot_notification'
    | 'charger.transaction_event'
    | 'charger.data_transfer'
    | 'charger.ocpp_logs'
    | 'vehicle.telemetry_received'
    | 'vehicle.optimization'
  )[]
}
export type UserGroupDto = {
  /** Unique identifier of the group */
  id: string
  /** Name of the group */
  name: string
  /** Organization ID that owns this group */
  organizationId: string
  /** Users that are members of this group */
  userIds?: string[]
}
export type UserGroupUpdateParams = {
  /** Name of the group */
  name: string
  /** ID of the organization that owns this group */
  organizationId: string
  /** Set of user IDs to be added as members of the group */
  userIds?: string[]
  /** ID of the group to update */
  id: string
}
export type Auth0ConnectionOrgMappingModel = {
  /** Internal mapping id */
  id?: string
  /** Auth0 connection id (e.g. con_abc123) */
  auth0ConnectionId?: string
  /** Internal organization UUID that SSO users on this connection are provisioned into */
  organizationId?: string
}
export type UpdateAuth0ConnectionOrgMappingRequest = {
  /** Internal organization UUID to JIT provision SSO users into */
  organizationId: string
}
export type MfaOptionType = object
export type UserTag = {
  created?: string
  updated?: string
  userId?: string
  ocppTagValue?: string
  ocppTagUseCase: 'OPERATIONS' | 'WORKPLACE_CHARGING' | 'FLEET_CHARGING' | 'REIMBURSEMENTS' | 'OTHER'
  ocppTagType: 'MOBILE' | 'VID' | 'RFID' | 'QR_CODE' | 'NFC' | 'OTHER' | 'OPERATIONS'
}
export type UserModel = {
  name?: string
  email?: string
  phoneNumber?: string
  organizationId?: string
  status?:
    | 'ARCHIVED'
    | 'COMPROMISED'
    | 'CONFIRMED'
    | 'DISABLED'
    | 'EXTERNAL_PROVIDER'
    | 'FORCE_CHANGE_PASSWORD'
    | 'RESET_PASSWORD'
    | 'RESET_REQUIRED'
    | 'UNCONFIRMED'
    | 'UNKNOWN'
  mfaEnabled?: boolean
  isActive?: boolean
  preferredDistanceUnits?: string
  preferredTemperatureUnits?: string
  preferredEfficiencyUnits?: string
  preferredTimeFormat?: string
  preferredDateFormat?: string
  preferredTimeZone?: string
  preferredLanguage?: 'en' | 'fr' | 'de'
  receiveEmailNotifications?: boolean
  receiveSMSNotifications?: boolean
  roles?: string[]
  /** Whether the user's role is read-only. Read-only roles keep their view access but are blocked from mutating operations. Ignored on self-updates. */
  readOnly?: boolean
  sites?: string[]
  mfaOptionTypes?: MfaOptionType[]
  termsAccepted?: boolean
  termsAcceptedDt?: string
  /** The status of MFA for the user. */
  mfaSetupStatus?: 'COMPLETED' | 'INCOMPLETE' | 'NOT_APPLICABLE' | 'ON'
  /** Assignable external ID for user reconciliation with external systems. */
  externalId?: string
  /** The preferred locale for the user */
  locale?: string
  /** Unique ID for a User */
  id: string
  rfidTags?: UserTag[]
  customerId?: string
  setupIntentId?: string
  /** Time of the user's most recent login (UTC) */
  lastLogin?: string
}
export type EditUserModel = {
  name?: string
  email?: string
  phoneNumber?: string
  organizationId?: string
  status?:
    | 'ARCHIVED'
    | 'COMPROMISED'
    | 'CONFIRMED'
    | 'DISABLED'
    | 'EXTERNAL_PROVIDER'
    | 'FORCE_CHANGE_PASSWORD'
    | 'RESET_PASSWORD'
    | 'RESET_REQUIRED'
    | 'UNCONFIRMED'
    | 'UNKNOWN'
  mfaEnabled?: boolean
  isActive?: boolean
  preferredDistanceUnits?: string
  preferredTemperatureUnits?: string
  preferredEfficiencyUnits?: string
  preferredTimeFormat?: string
  preferredDateFormat?: string
  preferredTimeZone?: string
  preferredLanguage?: 'en' | 'fr' | 'de'
  receiveEmailNotifications?: boolean
  receiveSMSNotifications?: boolean
  roles?: string[]
  /** Whether the user's role is read-only. Read-only roles keep their view access but are blocked from mutating operations. Ignored on self-updates. */
  readOnly?: boolean
  sites?: string[]
  mfaOptionTypes?: MfaOptionType[]
  termsAccepted?: boolean
  termsAcceptedDt?: string
  /** The status of MFA for the user. */
  mfaSetupStatus?: 'COMPLETED' | 'INCOMPLETE' | 'NOT_APPLICABLE' | 'ON'
  /** Assignable external ID for user reconciliation with external systems. */
  externalId?: string
  /** The preferred locale for the user */
  locale?: string
  /** Unique ID for a User */
  id: string
  rfidTags?: UserTag[]
}
export type CreateUserTagModel = {
  ocppTagValue?: string
  ocppTagUseCase?: 'OPERATIONS' | 'WORKPLACE_CHARGING' | 'FLEET_CHARGING' | 'REIMBURSEMENTS' | 'OTHER'
  ocppTagType?: 'MOBILE' | 'VID' | 'RFID' | 'QR_CODE' | 'NFC' | 'OTHER' | 'OPERATIONS'
}
export type CreateUserModel = {
  name?: string
  email?: string
  phoneNumber?: string
  organizationId?: string
  status?:
    | 'ARCHIVED'
    | 'COMPROMISED'
    | 'CONFIRMED'
    | 'DISABLED'
    | 'EXTERNAL_PROVIDER'
    | 'FORCE_CHANGE_PASSWORD'
    | 'RESET_PASSWORD'
    | 'RESET_REQUIRED'
    | 'UNCONFIRMED'
    | 'UNKNOWN'
  mfaEnabled?: boolean
  isActive?: boolean
  preferredDistanceUnits?: string
  preferredTemperatureUnits?: string
  preferredEfficiencyUnits?: string
  preferredTimeFormat?: string
  preferredDateFormat?: string
  preferredTimeZone?: string
  preferredLanguage?: 'en' | 'fr' | 'de'
  receiveEmailNotifications?: boolean
  receiveSMSNotifications?: boolean
  roles?: string[]
  /** Whether the user's role is read-only. Read-only roles keep their view access but are blocked from mutating operations. Ignored on self-updates. */
  readOnly?: boolean
  sites?: string[]
  mfaOptionTypes?: MfaOptionType[]
  termsAccepted?: boolean
  termsAcceptedDt?: string
  /** The status of MFA for the user. */
  mfaSetupStatus?: 'COMPLETED' | 'INCOMPLETE' | 'NOT_APPLICABLE' | 'ON'
  /** Assignable external ID for user reconciliation with external systems. */
  externalId?: string
  /** The preferred locale for the user */
  locale?: string
  rfidTags?: CreateUserTagModel[]
}
export type MoveUserModel = {
  /** Target organization ID to move the user to */
  targetOrganizationId: string
}
export type EventRecurrence = {
  created?: string
  updated?: string
  eventRecurrenceId?: string
  startDate?: string
  recurrenceRule?: string
  validUntilDate?: string
  recurrenceName?: string
  recurrenceType?: 'DAILY' | 'WEEKLY' | 'WEEKDAYS' | 'CUSTOM'
}
export type RouteComponent = {
  created?: string
  updated?: string
  id?: string
  routeId?: string
  routeComponentNm?: string
  routeComponentType?: 'ORIGIN' | 'POINT' | 'DESTINATION'
  locationType?: 'LOCATION_OF_INTEREST' | 'SITE' | 'POINT' | 'MAINTENANCE' | 'OTHER'
  locationId?: string
  precedence?: number
  latitude?: number
  longitude?: number
  address?: string
  componentDesc?: string
  distance?: number
  distanceUnits?: string
  duration?: number
  durationUnits?: string
  bearingBefore?: number
  bearingAfter?: number
  arrivalTime?: string
  departureTime?: string
  serviceDurationSeconds?: number
  orderKey?: string
}
export type Route = {
  created?: string
  updated?: string
  id?: string
  routeNm?: string
  routeType?: 'OPEN' | 'POINT_TO_POINT' | 'FROM_TELEMETRY'
  distance?: number
  distanceUnits?: string
  duration?: number
  durationUnits?: string
  weight?: number
  weightUnits?: string
  organizationId?: string
  fleetId?: string
  externalRouteRequestPayload?: string
  externalRoutePayloadObjectUrl?: string
  externalId?: string
  components?: RouteComponent[]
}
export type ScheduledRoute = {
  created?: string
  updated?: string
  scheduledRouteId?: string
  vehicleId?: string
  scheduledStart?: string
  actualStart?: string
  scheduledEnd?: string
  actualEnd?: string
  status?: 'SCHEDULED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED'
  type?: 'USER_CREATED' | 'PREDICTED' | 'GEOTAB' | 'ZONAR' | 'SAMSARA' | 'OTHER'
  routeId?: string
  driverId?: string
  weight?: number
  weightUnits?: string
  eventRecurrence?: EventRecurrence
  route?: Route
}
export type ScheduledRouteUpdateModel = {
  vehicleId: string
  scheduledRouteId: string
  status?: 'SCHEDULED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED'
  scheduledRouteType?: 'USER_CREATED' | 'PREDICTED' | 'GEOTAB' | 'ZONAR' | 'SAMSARA' | 'OTHER'
  actualStart?: string
  actualEnd?: string
  scheduledStart?: string
  scheduledEnd?: string
  /** True if the entire recurrence should be updated, false if only the attached scheduledRouteId should be updated */
  updateRecurrence?: boolean
  driverId?: string
  weight?: number
  weightUnits?: string
}
export type OrganizationModel = {
  /** Organization Name */
  organizationNm: string
  /** Organization Type */
  organizationType?: string
  /** Organization Logo Asset. This is used as the logo in the login pages, navigation and mobile. These can be uploaded and cropped in the platform. */
  logoUrl?: string
  /** Icon URL used in the web application navigation and mobile app loading views */
  iconUrl?: string
  /** Light-mode Organization Logo Asset; kept in sync with logoUrl during the transition to per-theme assets. Ignored on organization update — managed by the logo upload/delete endpoints. */
  logoUrlLight?: string
  /** Dark-mode Organization Logo Asset; null means clients fall back to the light logo. Ignored on organization update — managed by the logo upload/delete endpoints. */
  logoUrlDark?: string
  /** Light-mode Icon URL; kept in sync with iconUrl during the transition to per-theme assets. Ignored on organization update — managed by the icon upload/delete endpoints. */
  iconUrlLight?: string
  /** Dark-mode Icon URL; null means clients fall back to the light icon. Ignored on organization update — managed by the icon upload/delete endpoints. */
  iconUrlDark?: string
  /** Organization Color Scheme. This is used to set the color scheme for the organization. This can be set in the platform. */
  styleOverrides?: string
  /** Determines whether Ops Center is Enabled */
  hasOpsCenter?: boolean
  /** Determines whether Reporting is Enabled */
  hasReporting?: boolean
  /** Determines whether Billing is Enabled */
  hasBilling?: boolean
  /** Determines whether Alarms are Enabled */
  hasAlarms?: boolean
  /** The connected account for this Organization */
  connectedAccountId?: string
  /** Determines whether the connected account is active */
  connectedBillingEnabled?: boolean
  /** Customer ID charges are allocated to for billing purposes */
  customerId?: string
  /** Setup Intent ID for the Organization Payment Method */
  setupIntentId?: string
  /** Determines whether the Organization has successfully completed onboarding */
  hasCompletedOnboarding?: boolean
  /** Determines whether the Organization has Marketplace enabled */
  hasMarketplace?: boolean
  /** Determines whether the Organization has Energy Management enabled */
  hasEnergyManagement?: boolean
  /** Determines whether the Organization has Charge Management enabled */
  hasChargeManagement?: boolean
  /** Determines whether the Organization has Fleet Management enabled */
  hasFleetManagement?: boolean
  /** Determines whether the Organization has Home Charging & Reimbursements enabled */
  hasReimbursements?: boolean
  /** Determines whether the Organization has Cost Explorer enabled */
  hasCostExplorer?: boolean
  /** Determines whether the Organization has Fleet Billing enabled */
  hasFleetBilling?: boolean
  /** Determines whether the Organization has Public Charging enabled */
  hasPublicCharging?: boolean
  /** Determines whether the Organization has V2G enabled */
  hasV2G?: boolean
  /** Determines whether the Organization has Synop Link enabled */
  hasSynopLink?: boolean
  /** Determines whether the Organization has Synop Autopilot enabled */
  hasSynopAutopilot?: boolean
  /** Status of Fleet Billing for the Organization */
  fleetBillingStatus?: 'NEVER_ENABLED' | 'ENABLED' | 'DISABLED'
  /** Status of Public Charging for the Organization */
  publicChargingStatus?: 'NEVER_ENABLED' | 'ENABLED' | 'DISABLED'
  /** Status of Reimbursements for the Organization */
  reimbursementsStatus?: 'NEVER_ENABLED' | 'ENABLED' | 'DISABLED'
  /** Determines whether the Organization has Workplace Charging enabled */
  hasWorkplaceCharging?: boolean
  /** Status of Workplace Charging for the Organization */
  workplaceChargingStatus?: 'NEVER_ENABLED' | 'ENABLED' | 'DISABLED'
  /** Status of the Organization Billing Account */
  connectedAccountStatus?:
    | 'RESTRICTED'
    | 'RESTRICTED_SOON'
    | 'PENDING'
    | 'ENABLED'
    | 'COMPLETE'
    | 'REJECTED'
    | 'UNKNOWN'
  /** Custom login URL to redirect to */
  loginUrl?: string
  /** Determines whether the Organization is active */
  active?: boolean
  /** App Client ID used for SSO Authentication */
  appClientId?: string
  /** Determines whether the Organization has SSO enabled */
  hasSSO?: boolean
  /** The status of MFA for an organization. */
  mfaStatus?: 'OFF' | 'ON_REQUIRED' | 'ON_NOT_REQUIRED'
  /** iOS App Store URL */
  iosAppStoreUrl?: string
  /** Icon URL used in the web application navigation and mobile app loading views */
  androidPlayStoreUrl?: string
  /** Determines whether the Organization has Insights enabled */
  hasInsights?: boolean
  /** External Id mapped to the organization */
  externalId?: string
  /** The preferred locale of the organization */
  locale?: string
  /** The preferred language of the organization */
  preferredLanguage?: 'en' | 'fr' | 'de'
  /** Which uptime metric is displayed as the primary uptime across the platform for this Organization. INHERIT (the default) means the organization defers to its parent organization's setting (or the platform default). On update, omitting the field (null) leaves the stored value unchanged. */
  primaryUptimeDisplay?: string
  /** Whether this organization allows users to sign up with email addresses outside of the configured SSO email domains */
  allowOutsideEmails?: boolean
  /** Determines whether the Organization has access to Products and Agreements */
  hasProductsAndAgreements?: boolean
  /** Determines whether the Organization can view utility tariff information */
  hasUtilityTariff?: boolean
  /** Determines whether the Organization has TMS enabled */
  hasTMS?: boolean
  /** Unique ID for an Organization */
  id: string
  fleetsCount?: number
  chargersCount?: number
  vehiclesCount?: number
  usersCount?: number
  hasCustomLogo: boolean
  hasCustomIcon: boolean
  /** UI theme mode (day/night) for the Organization */
  uiTheme?: 'DAY' | 'NIGHT'
  /** UI accent color for the Organization */
  uiAccentColor?: string
  /** Whether child Organizations can edit their theme settings */
  allowChildThemeEdits?: boolean
  /** Whether this Organization can edit its theme settings */
  canEditTheme?: boolean
  /** The uptime metric effectively displayed for this Organization after resolving INHERIT up the parent chain — never INHERIT. */
  effectivePrimaryUptimeDisplay?: string
  /** Whether the acting user may change this Organization's primaryUptimeDisplay — true for ancestor organizations acting on a descendant, and for top-level organizations editing themselves. */
  canEditPrimaryUptimeDisplay?: boolean
  /** WebSocket URL for the Organization */
  websocketUrl?: string
  /** Whether users in this Organization can be added or managed through Synop */
  allowUserManagement?: boolean
  /** The Auth0 connection ID for this Organization */
  ssoConnectionId?: string
  /** The email domains that are configured for SSO for this Organization */
  ssoEmailDomains?: string
  /** The Auth0 tenant id for this Organization */
  auth0Tenant?: string
  /** Determines whether the Organization has Workflows enabled */
  hasWorkflows?: boolean
  /** Determines whether the Organization has Operations enabled. */
  hasOperations?: boolean
}
export type EditOrganizationModel = {
  /** Organization Name */
  organizationNm: string
  /** Organization Type */
  organizationType?: string
  /** Organization Logo Asset. This is used as the logo in the login pages, navigation and mobile. These can be uploaded and cropped in the platform. */
  logoUrl?: string
  /** Icon URL used in the web application navigation and mobile app loading views */
  iconUrl?: string
  /** Light-mode Organization Logo Asset; kept in sync with logoUrl during the transition to per-theme assets. Ignored on organization update — managed by the logo upload/delete endpoints. */
  logoUrlLight?: string
  /** Dark-mode Organization Logo Asset; null means clients fall back to the light logo. Ignored on organization update — managed by the logo upload/delete endpoints. */
  logoUrlDark?: string
  /** Light-mode Icon URL; kept in sync with iconUrl during the transition to per-theme assets. Ignored on organization update — managed by the icon upload/delete endpoints. */
  iconUrlLight?: string
  /** Dark-mode Icon URL; null means clients fall back to the light icon. Ignored on organization update — managed by the icon upload/delete endpoints. */
  iconUrlDark?: string
  /** Organization Color Scheme. This is used to set the color scheme for the organization. This can be set in the platform. */
  styleOverrides?: string
  /** Determines whether Ops Center is Enabled */
  hasOpsCenter?: boolean
  /** Determines whether Reporting is Enabled */
  hasReporting?: boolean
  /** Determines whether Billing is Enabled */
  hasBilling?: boolean
  /** Determines whether Alarms are Enabled */
  hasAlarms?: boolean
  /** The connected account for this Organization */
  connectedAccountId?: string
  /** Determines whether the connected account is active */
  connectedBillingEnabled?: boolean
  /** Customer ID charges are allocated to for billing purposes */
  customerId?: string
  /** Setup Intent ID for the Organization Payment Method */
  setupIntentId?: string
  /** Determines whether the Organization has successfully completed onboarding */
  hasCompletedOnboarding?: boolean
  /** Determines whether the Organization has Marketplace enabled */
  hasMarketplace?: boolean
  /** Determines whether the Organization has Energy Management enabled */
  hasEnergyManagement?: boolean
  /** Determines whether the Organization has Charge Management enabled */
  hasChargeManagement?: boolean
  /** Determines whether the Organization has Fleet Management enabled */
  hasFleetManagement?: boolean
  /** Determines whether the Organization has Home Charging & Reimbursements enabled */
  hasReimbursements?: boolean
  /** Determines whether the Organization has Cost Explorer enabled */
  hasCostExplorer?: boolean
  /** Determines whether the Organization has Fleet Billing enabled */
  hasFleetBilling?: boolean
  /** Determines whether the Organization has Public Charging enabled */
  hasPublicCharging?: boolean
  /** Determines whether the Organization has V2G enabled */
  hasV2G?: boolean
  /** Determines whether the Organization has Synop Link enabled */
  hasSynopLink?: boolean
  /** Determines whether the Organization has Synop Autopilot enabled */
  hasSynopAutopilot?: boolean
  /** Status of Fleet Billing for the Organization */
  fleetBillingStatus?: 'NEVER_ENABLED' | 'ENABLED' | 'DISABLED'
  /** Status of Public Charging for the Organization */
  publicChargingStatus?: 'NEVER_ENABLED' | 'ENABLED' | 'DISABLED'
  /** Status of Reimbursements for the Organization */
  reimbursementsStatus?: 'NEVER_ENABLED' | 'ENABLED' | 'DISABLED'
  /** Determines whether the Organization has Workplace Charging enabled */
  hasWorkplaceCharging?: boolean
  /** Status of Workplace Charging for the Organization */
  workplaceChargingStatus?: 'NEVER_ENABLED' | 'ENABLED' | 'DISABLED'
  /** Status of the Organization Billing Account */
  connectedAccountStatus?:
    | 'RESTRICTED'
    | 'RESTRICTED_SOON'
    | 'PENDING'
    | 'ENABLED'
    | 'COMPLETE'
    | 'REJECTED'
    | 'UNKNOWN'
  /** Custom login URL to redirect to */
  loginUrl?: string
  /** Determines whether the Organization is active */
  active?: boolean
  /** App Client ID used for SSO Authentication */
  appClientId?: string
  /** Determines whether the Organization has SSO enabled */
  hasSSO?: boolean
  /** The status of MFA for an organization. */
  mfaStatus?: 'OFF' | 'ON_REQUIRED' | 'ON_NOT_REQUIRED'
  /** iOS App Store URL */
  iosAppStoreUrl?: string
  /** Icon URL used in the web application navigation and mobile app loading views */
  androidPlayStoreUrl?: string
  /** Determines whether the Organization has Insights enabled */
  hasInsights?: boolean
  /** External Id mapped to the organization */
  externalId?: string
  /** The preferred locale of the organization */
  locale?: string
  /** The preferred language of the organization */
  preferredLanguage?: 'en' | 'fr' | 'de'
  /** Which uptime metric is displayed as the primary uptime across the platform for this Organization. INHERIT (the default) means the organization defers to its parent organization's setting (or the platform default). On update, omitting the field (null) leaves the stored value unchanged. */
  primaryUptimeDisplay?: string
  /** Whether this organization allows users to sign up with email addresses outside of the configured SSO email domains */
  allowOutsideEmails?: boolean
  /** Determines whether the Organization has access to Products and Agreements */
  hasProductsAndAgreements?: boolean
  /** Determines whether the Organization can view utility tariff information */
  hasUtilityTariff?: boolean
  /** Determines whether the Organization has TMS enabled */
  hasTMS?: boolean
  /** Unique ID for an Organization */
  id: string
  /** UI theme for the organization */
  uiTheme?: 'DAY' | 'NIGHT'
  /** UI accent color for the organization */
  uiAccentColor?: string
  /** Whether child organizations can customize their theme */
  allowChildThemeEdits?: boolean
  /** Whether theme edits should be applied to child organizations */
  applyThemeEditsToChildren?: boolean
  /** Whether locale edits should be applied to child organizations */
  applyLocaleEditsToChildren?: boolean
  /** Whether language edits should be applied to child organizations */
  applyLanguageEditsToChildren?: boolean
}
export type OrganizationTerms = {
  created?: string
  updated?: string
  id?: string
  organizationId?: string
  documentTitle?: string
  effectiveDt?: string
  terms?: string
}
export type LocationOfInterestResponseModel = {
  /** A name describing for the Location of Interest */
  loiNm: string
  /** A type for the Location of Interest */
  loiType: 'Maintenance' | 'Port' | 'Residence' | 'Other'
  /** Address for the Location of Interest */
  address: string
  /** Latitude for the Location of Interest */
  latitude: number
  /** Longitude for the Location of Interest */
  longitude: number
  /** The id of the Organization that the Location of Interest belongs to */
  organizationId: string
  /** Geofence for the Location of Interest */
  geofence: string
  /** An unique id for the Location of Interest */
  id: string
}
export type LocationOfInterestUpdateModel = {
  /** An unique id for the Location of Interest */
  id: string
  /** A name describing for the Location of Interest */
  loiNm: string
  /** A type for the Location of Interest */
  loiType: 'Maintenance' | 'Port' | 'Residence' | 'Other'
  /** Address for the Location of Interest */
  address: string
  /** Latitude for the Location of Interest */
  latitude: number
  /** Longitude for the Location of Interest */
  longitude: number
  /** The id of the Organization that the Location of Interest belongs to */
  organizationId: string
  /** Geofence for the Location of Interest */
  geofence: string
}
export type LocationOfInterestCreateModel = {
  /** A name describing for the Location of Interest */
  loiNm: string
  /** A type for the Location of Interest */
  loiType: 'Maintenance' | 'Port' | 'Residence' | 'Other'
  /** Address for the Location of Interest */
  address: string
  /** Latitude for the Location of Interest */
  latitude: number
  /** Longitude for the Location of Interest */
  longitude: number
  /** The id of the Organization that the Location of Interest belongs to */
  organizationId: string
  /** Geofence for the Location of Interest */
  geofence: string
}
export type FleetModel = {
  /** The name for a Fleet */
  fleetNm?: string
  /** The organizationId for a Fleet */
  organizationId: string
  /** A flag that represents if a Fleet is active or not */
  active?: boolean
  /** An unique id for a Fleet */
  id: string
  /** A list of vehicles assigned to this fleet */
  vehicles?: string[]
}
export type PagedResponseV2FleetModel = {
  meta: Meta
  data: FleetModel[]
}
export type CreateFleetModel = {
  /** The name for a Fleet */
  fleetNm?: string
  /** The organizationId for a Fleet */
  organizationId: string
  /** A flag that represents if a Fleet is active or not */
  active?: boolean
  /** A list of vehicles assigned to this fleet */
  vehicles?: string[]
}
export type VehicleEvent = {
  created?: string
  updated?: string
  id?: string
  vehicleId?: string
  eventType?: string
  scheduledStart?: string
  scheduledEnd?: string
  status?: string
  eventTargetNm?: string
  routeId?: string
  actorId?: string
}
export type VehicleTelemetryCreateParams = {
  /** Unique identifier for the vehicle */
  vehicleId: string
  /** State of Charger of the vehicle in percent */
  stateOfCharge?: number
  /** Latitude of the vehicle in degrees */
  latitude?: number
  /** Longitude of the vehicle in degrees */
  longitude?: number
  /** Voltage of the vehicle's 12V battery system in volts */
  batteryPotential?: number
  /** Ambient air temperature at the vehicle's location in degrees Celsius */
  ambientAirTemperature?: number
  /** Odometer reading of the vehicle in kilometers */
  odometer?: number
  /** Operational status (e.g. In Motion / Idle / Plugged In) from a self-reporting source */
  vehicleStatus?: string
}
export type TelematicsCredentialValidation = {
  /** Whether the supplied credentials are valid */
  valid?: boolean
  /** Human-readable reason when validation fails; null on success */
  reason?: string
}
export type TelematicsCredentialValidationRequest = {
  /** ID of the integration whose credentials are being validated */
  integrationId: string
  /** Telematics provider name; selects the validation implementation */
  provider: string
  /** Provider-specific credentials. */
  configuration: {
    [key: string]: string
  }
}
export type UserGroupCreateParams = {
  /** Name of the group */
  name: string
  /** ID of the organization that owns this group */
  organizationId: string
  /** Set of user IDs to be added as members of the group */
  userIds?: string[]
}
export type TicketPrefixBackfillResult = {
  /** Zero-based page of live organizations this call processed */
  page?: number
  /** Organizations this call attempted */
  processed?: number
  /** Organizations that now have a stored prefix (already had one, or syn-ops derived one on this call) */
  assigned?: number
  /** Organizations where the syn-ops call failed or returned no prefix; re-run this page later to pick them up again */
  failed?: number
  /** Organizations on later pages, still unprocessed after this call */
  remaining?: number
  /** Page to request next, or null when this was the last page */
  nextPage?: number
  /** Ids of organizations whose assignment failed on this call */
  failedOrganizationIds?: string[]
}
export type StagedVehicleModel = {
  /** Staged vehicle ID */
  id: string
  /** Vehicle Identification Number */
  vin?: string
  /** External vehicle ID from the telematics provider */
  externalVehicleId?: string
  /** External device ID from the telematics provider */
  externalDeviceId?: string
  /** ID of the integration this vehicle was staged from */
  integrationId?: string
  /** Staging status */
  status?: 'PENDING_ACTION' | 'APPROVED' | 'REJECTED'
  /** Vehicle name from the provider */
  vehicleName?: string
  /** Serial number */
  serialNumber?: string
  /** User-provided VIN */
  userVin?: string
  /** Activation date/time */
  activeDateTime?: string
  /** Expiration date/time */
  expirationDateTime?: string
  /** Device name */
  deviceName?: string
  /** Vehicle make */
  make?: string
  /** Vehicle model */
  model?: string
  /** Vehicle model year */
  modelYear?: number
  /** Battery capacity */
  batteryCapacity?: number
  /** Estimated range */
  estimatedRange?: number
  /** ID of the onboarded Synop vehicle, if any */
  synopVehicleId?: string
  /** Timestamp of the last sync with the provider */
  lastSyncTimestamp?: string
}
export type PagedResponseV2StagedVehicleModel = {
  meta: Meta
  data: StagedVehicleModel[]
}
export type StagedVehicle = {
  created?: string
  updated?: string
  id?: string
  vin?: string
  externalVehicleId?: string
  externalDeviceId?: string
  integrationId?: string
  status?: 'PENDING_ACTION' | 'APPROVED' | 'REJECTED'
  vehicleName?: string
  serialNumber?: string
  userVin?: string
  activeDateTime?: string
  expirationDateTime?: string
  deviceName?: string
  make?: string
  model?: string
  modelYear?: number
  batteryCapacity?: number
  estimatedRange?: number
  synopVehicleId?: string
  lastSyncTimestamp?: string
}
export type SharingResponseModel = {
  id?: string
  granteeOrganizationId?: string
  granteeOrganizationName?: string
  grantorOrganizationId?: string
  grantorOrganizationName?: string
  sharingType?: string
  assetId?: string
  assetName?: string
  status?: string
  requestedAt?: string
  approvedAt?: string
  revokedAt?: string
}
export type InviteCodeResponseModel = {
  id?: string
  code?: string
  granteeOrganizationId?: string
  granteeOrganizationName?: string
  status?: string
  expiresAt?: string
  redeemedAt?: string
  redeemedByOrganizationId?: string
  redeemedByOrganizationName?: string
}
export type OfferOrgsRequestModel = {
  /** IDs of organizations to share */
  orgIds: string[]
}
export type RedeemCodeRequestModel = {
  /** The 8-character invite code */
  code: string
}
export type OnboardVehiclesRequest = {
  /** List of staged vehicle IDs to approve for onboarding */
  approve?: string[]
  /** List of staged vehicle IDs to reject */
  reject?: string[]
  /** List of staged vehicle IDs to delete. Deletes the onboarded vehicle and its related records. */
  delete?: string[]
}
export type CreateAuth0ConnectionOrgMappingRequest = {
  /** Auth0 connection id */
  auth0ConnectionId: string
  /** Internal organization UUID to JIT provision SSO users into */
  organizationId: string
}
export type MfaModel = {
  secretCode: string
}
export type AuthProviderLookupResponse = {
  /** Synop internal user UUID */
  userId: string
  /** Synop organization ID the user belongs to */
  organizationId: string
}
export type AuthProviderLookupRequest = {
  /** The Auth0 subject identifier (sub claim) */
  sub: string
  /** The Auth0 tenant identifier */
  tenant: string
}
export type UserIdentityResponse = {
  /** The internal user ID */
  userId: string
}
export type SsoProvisioningProfile = {
  /** SSO provider asserting this profile */
  provider?: string
  /** Provider-side identifier of the user, stored as the user's externalId */
  externalId?: string
  /** Provider-side organization identifier, matched against the externalId of an organization inside the connection's organization tree. A login without a single active match is denied. */
  externalOrganizationId?: string
  /** Provider-side role names, translated through the configured role map. Empty grants the configured default role. */
  roles?: string[]
  /** Provider-side site identifiers, matched against depot externalIds. Empty grants access to all sites. */
  sites?: string[]
  /** Given name of the user */
  givenName?: string
  /** Family name of the user */
  familyName?: string
}
export type UserIdentityRequest = {
  /** The auth provider identifier (sub claim from Auth0) */
  sub: string
  /** Email address of the user */
  email: string
  /** The Auth0 tenant identifier */
  tenant: string
  /** Full name of the user */
  name?: string
  /** Organization ID to associate the user with */
  organization: string
  /** Phone number of the user */
  phoneNumber?: string
  /** Auth0 connection id the user authenticated through; used to look up an Auth0ConnectionOrgMapping override */
  connection?: string
  /** Provider-asserted access profile. When present the provider is authoritative for the user's organization, roles and sites, and the request is handled by SSO provisioning instead of lookup-or-create. */
  provisioning?: SsoProvisioningProfile
}
export type VehicleFault = {
  created?: string
  updated?: string
  id?: string
  vehicleId?: string
  timestamp?: string
  sourceAddress?: number
  sourceName?: string
  sourceLabel?: string
  spn?: number
  spnName?: string
  spnDescription?: string
  fmi?: number
  fmiText?: string
  occurrenceCount?: number
  milLampOn?: boolean
  amberLampOn?: boolean
  redLampOn?: boolean
  protectLampOn?: boolean
  dtcId?: string
  dtcShortCode?: string
  dtcDescription?: string
  latitude?: number
  longitude?: number
  provider?: string
}
export type Trip = {
  created?: string
  updated?: string
  id?: string
  tripStatus?: string
  vehicleId?: string
  fleetId?: string
  averageSpeed?: number
  speedUnit?: string
  distance?: number
  distanceUnit?: string
  emissionsSave?: number
  emissionsUnit?: string
  routeId?: string
  routeStart?: string
  routeEnd?: string
  efficiency?: number
  efficiencyUnit?: string
  startSoc?: number
  endSoc?: number
  startLatitude?: number
  endLatitude?: number
  startLongitude?: number
  endLongitude?: number
  driverId?: string
  originNm?: string
  destinationNm?: string
  source?: string
  energyUsed?: number
  faults?: VehicleFault[]
}
export type SimTripCreateParams = {
  vehicleId: string
  routeStart: string
  routeEnd: string
  startSoc: number
  endSoc: number
  distance: number
  energyUsed?: number
  startLatitude?: number
  startLongitude?: number
  endLatitude?: number
  endLongitude?: number
}
export type TripNote = {
  created?: string
  updated?: string
  id?: string
  tripId?: string
  fromUserId?: string
  content?: string
  senderName?: string
}
export type ScheduledRouteModel = {
  vehicleId?: string
  scheduledRouteId?: string
  routeId?: string
  status?: 'SCHEDULED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED'
  scheduledStart?: string
  scheduledEnd?: string
  actualStart?: string
  actualEnd?: string
  distance?: number
  distanceUnit?: string
  duration?: number
  durationUnit?: string
  weight?: number
  weightUnit?: string
  driverId?: string
  vehicle?: VehicleModel
  route?: Route
}
export type PagedResponseV1ScheduledRouteModel = {
  meta: Meta
  payload: ScheduledRouteModel[]
}
export type ScheduleRouteResponse = {
  succeeded?: string[]
  failed?: string[]
}
export type EventRecurrenceModel = {
  eventRecurrenceId?: string
  recurrenceRule?: string
  startDate?: string
  validUntilDate?: string
  recurrenceName?: string
  recurrenceType?: 'DAILY' | 'WEEKLY' | 'WEEKDAYS' | 'CUSTOM'
}
export type ScheduleRouteRequest = {
  /** Route to schedule vehicles against */
  routeId: string
  scheduledRouteType?: 'USER_CREATED' | 'PREDICTED' | 'GEOTAB' | 'ZONAR' | 'SAMSARA' | 'OTHER'
  scheduledStart: string
  scheduledEnd?: string
  /** RRule describing the recurrence of the event if applicable */
  eventRecurrence?: EventRecurrenceModel
  /** List of vehicle IDs to attach this route and schedule to */
  vehicleIds?: string[]
  /** Create this schedule for all vehicles at the provided site */
  siteId?: string
  fleetId?: string
  organizationId?: string
  /** Expected weight of the vehicle and its contents */
  weight?: number
  /** Weight units for the vehicle and its contents */
  weightUnits?: number
}
export type StringResponseModel = {
  value?: string
}
export type UrlRedirectModel = {
  baseUrl?: string
  path?: string
  parameters?: string
}
export type SsoConfigResponse = {
  displayName?: string
  strategy?: string
  realms?: string[]
  showAsButton?: boolean
  assignMembershipOnLogin?: boolean
  domainAliases?: string[]
  domain?: string
  tenantDomain?: string
  scope?: string[]
  email?: boolean
  profile?: boolean
  idpInitiated?: {
    [key: string]: object
  }
  additionalOptions?: {
    [key: string]: object
  }
}
export type Auth0SsoTicketResponse = {
  ticket?: string
}
export type TransferSsoConfigRequest = {
  /** Target organization UUID to transfer the SSO configuration to */
  targetOrganizationId: string
}
export type OrganizationApiKeyModel = {
  id?: string
  organizationId: string
  apiKey: string
  awsKeyId: string
  active?: boolean
  usagePlanId?: string
  label?: string
  keyLast4?: string
  created?: string
  updated?: string
}
export type OrganizationApiKeyCreateParams = {
  /** Existing API Gateway usage plan id to attach the key to */
  usagePlanId?: string
  /** Human-readable label shown in the admin console */
  label?: string
  /** EXTERNAL for a key the organization uses itself, INTERNAL for a Synop or integration key */
  keyType?: 'EXTERNAL' | 'INTERNAL'
}
export type OrganizationIntegration = {
  created?: string
  updated?: string
  id?: string
  organizationId?: string
  integrationId?: string
  externalId?: string
  organizationManagementIntegrationId?: string
  autoOnboard?: boolean
  ingestVehicles?: boolean
  ingestTelemetry?: boolean
  ingestTrips?: boolean
  ingestChargingTelemetry?: boolean
  ingestFaults?: boolean
  active?: boolean
  metadata?: string
}
export type CreateOrganizationModel = {
  /** Organization Name */
  organizationNm: string
  /** Organization Type */
  organizationType?: string
  /** Organization Logo Asset. This is used as the logo in the login pages, navigation and mobile. These can be uploaded and cropped in the platform. */
  logoUrl?: string
  /** Icon URL used in the web application navigation and mobile app loading views */
  iconUrl?: string
  /** Light-mode Organization Logo Asset; kept in sync with logoUrl during the transition to per-theme assets. Ignored on organization update — managed by the logo upload/delete endpoints. */
  logoUrlLight?: string
  /** Dark-mode Organization Logo Asset; null means clients fall back to the light logo. Ignored on organization update — managed by the logo upload/delete endpoints. */
  logoUrlDark?: string
  /** Light-mode Icon URL; kept in sync with iconUrl during the transition to per-theme assets. Ignored on organization update — managed by the icon upload/delete endpoints. */
  iconUrlLight?: string
  /** Dark-mode Icon URL; null means clients fall back to the light icon. Ignored on organization update — managed by the icon upload/delete endpoints. */
  iconUrlDark?: string
  /** Organization Color Scheme. This is used to set the color scheme for the organization. This can be set in the platform. */
  styleOverrides?: string
  /** Determines whether Ops Center is Enabled */
  hasOpsCenter?: boolean
  /** Determines whether Reporting is Enabled */
  hasReporting?: boolean
  /** Determines whether Billing is Enabled */
  hasBilling?: boolean
  /** Determines whether Alarms are Enabled */
  hasAlarms?: boolean
  /** The connected account for this Organization */
  connectedAccountId?: string
  /** Determines whether the connected account is active */
  connectedBillingEnabled?: boolean
  /** Customer ID charges are allocated to for billing purposes */
  customerId?: string
  /** Setup Intent ID for the Organization Payment Method */
  setupIntentId?: string
  /** Determines whether the Organization has successfully completed onboarding */
  hasCompletedOnboarding?: boolean
  /** Determines whether the Organization has Marketplace enabled */
  hasMarketplace?: boolean
  /** Determines whether the Organization has Energy Management enabled */
  hasEnergyManagement?: boolean
  /** Determines whether the Organization has Charge Management enabled */
  hasChargeManagement?: boolean
  /** Determines whether the Organization has Fleet Management enabled */
  hasFleetManagement?: boolean
  /** Determines whether the Organization has Home Charging & Reimbursements enabled */
  hasReimbursements?: boolean
  /** Determines whether the Organization has Cost Explorer enabled */
  hasCostExplorer?: boolean
  /** Determines whether the Organization has Fleet Billing enabled */
  hasFleetBilling?: boolean
  /** Determines whether the Organization has Public Charging enabled */
  hasPublicCharging?: boolean
  /** Determines whether the Organization has V2G enabled */
  hasV2G?: boolean
  /** Determines whether the Organization has Synop Link enabled */
  hasSynopLink?: boolean
  /** Determines whether the Organization has Synop Autopilot enabled */
  hasSynopAutopilot?: boolean
  /** Status of Fleet Billing for the Organization */
  fleetBillingStatus?: 'NEVER_ENABLED' | 'ENABLED' | 'DISABLED'
  /** Status of Public Charging for the Organization */
  publicChargingStatus?: 'NEVER_ENABLED' | 'ENABLED' | 'DISABLED'
  /** Status of Reimbursements for the Organization */
  reimbursementsStatus?: 'NEVER_ENABLED' | 'ENABLED' | 'DISABLED'
  /** Determines whether the Organization has Workplace Charging enabled */
  hasWorkplaceCharging?: boolean
  /** Status of Workplace Charging for the Organization */
  workplaceChargingStatus?: 'NEVER_ENABLED' | 'ENABLED' | 'DISABLED'
  /** Status of the Organization Billing Account */
  connectedAccountStatus?:
    | 'RESTRICTED'
    | 'RESTRICTED_SOON'
    | 'PENDING'
    | 'ENABLED'
    | 'COMPLETE'
    | 'REJECTED'
    | 'UNKNOWN'
  /** Custom login URL to redirect to */
  loginUrl?: string
  /** Determines whether the Organization is active */
  active?: boolean
  /** App Client ID used for SSO Authentication */
  appClientId?: string
  /** Determines whether the Organization has SSO enabled */
  hasSSO?: boolean
  /** The status of MFA for an organization. */
  mfaStatus?: 'OFF' | 'ON_REQUIRED' | 'ON_NOT_REQUIRED'
  /** iOS App Store URL */
  iosAppStoreUrl?: string
  /** Icon URL used in the web application navigation and mobile app loading views */
  androidPlayStoreUrl?: string
  /** Determines whether the Organization has Insights enabled */
  hasInsights?: boolean
  /** External Id mapped to the organization */
  externalId?: string
  /** The preferred locale of the organization */
  locale?: string
  /** The preferred language of the organization */
  preferredLanguage?: 'en' | 'fr' | 'de'
  /** Which uptime metric is displayed as the primary uptime across the platform for this Organization. INHERIT (the default) means the organization defers to its parent organization's setting (or the platform default). On update, omitting the field (null) leaves the stored value unchanged. */
  primaryUptimeDisplay?: string
  /** Whether this organization allows users to sign up with email addresses outside of the configured SSO email domains */
  allowOutsideEmails?: boolean
  /** Determines whether the Organization has access to Products and Agreements */
  hasProductsAndAgreements?: boolean
  /** Determines whether the Organization can view utility tariff information */
  hasUtilityTariff?: boolean
  /** Determines whether the Organization has TMS enabled */
  hasTMS?: boolean
}
export type PatchOrganizationFeaturesResult = {
  /** How many descendant Organizations the request cascaded to; 0 when nothing cascaded */
  cascadedOrganizationCount?: number
}
export type OrganizationFeaturesPatch = {
  /** Determines whether the Organization has Workflows enabled */
  hasWorkflows?: boolean
  /** Determines whether the Organization has Operations enabled. */
  hasOperations?: boolean
  /** Determines whether the Organization has Cost Explorer enabled */
  hasCostExplorer?: boolean
  /** Determines whether the Organization can view utility tariff information */
  hasUtilityTariff?: boolean
  /** Determines whether the Organization has Insights enabled */
  hasInsights?: boolean
  /** Determines whether the Organization has Energy Management enabled */
  hasEnergyManagement?: boolean
  /** Determines whether the Organization has V2G enabled */
  hasV2G?: boolean
  /** Determines whether the Organization has Public Charging enabled */
  hasPublicCharging?: boolean
  /** Determines whether the Organization has Home Charging & Reimbursements enabled */
  hasReimbursements?: boolean
  /** Determines whether Reporting is Enabled */
  hasReporting?: boolean
  /** Determines whether the Organization has access to Products and Agreements */
  hasProductsAndAgreements?: boolean
  /** Determines whether the Organization has Fleet Billing enabled */
  hasFleetBilling?: boolean
  /** Determines whether the Organization has Workplace Charging enabled */
  hasWorkplaceCharging?: boolean
  /** Determines whether the Organization has Synop Link enabled */
  hasSynopLink?: boolean
  /** Determines whether the Organization has Synop Autopilot enabled */
  hasSynopAutopilot?: boolean
  /** Determines whether the Organization has TMS enabled */
  hasTMS?: boolean
}
export type PatchOrganizationFeaturesModel = {
  /** The feature entitlements to change. Every other Organization field keeps its stored value. */
  features?: OrganizationFeaturesPatch
  /** Whether the features in this request are also written to every Organization beneath this one. Only features whose relationships to the other entitlements are enforced by the API may be cascaded; any other feature is rejected with a 400. */
  applyToChildren?: boolean
}
export type WeatherForecast = {
  ambientAirTemperature?: SourceAttributeValueBigDecimal
  humidity?: SourceAttributeValueBigDecimal
  pressure?: SourceAttributeValueBigDecimal
  windSpeed?: SourceAttributeValueBigDecimal
  windDirectionDegrees?: SourceAttributeValueBigDecimal
  windGust?: SourceAttributeValueBigDecimal
  firstWeatherConditionId?: SourceAttributeValueBigDecimal
  firstWeatherParameter?: SourceAttributeValueString
  secondWeatherConditionId?: SourceAttributeValueBigDecimal
  secondWeatherParameter?: SourceAttributeValueString
  thirdWeatherConditionId?: SourceAttributeValueBigDecimal
  thirdWeatherParameter?: SourceAttributeValueString
  cloudiness?: SourceAttributeValueBigDecimal
  visibility?: SourceAttributeValueBigDecimal
  partOfDay?: SourceAttributeValueString
  precipitationProbability?: SourceAttributeValueBigDecimal
}
export type VehicleWeatherForecast = {
  current?: WeatherForecast
  oneHourLead?: WeatherForecast
  twoHourLead?: WeatherForecast
  threeHourLead?: WeatherForecast
  fourHourLead?: WeatherForecast
  fiveHourLead?: WeatherForecast
}
export type AssetTelematics = {
  /** The unique identifier of the vehicle, maps to the Synop-generated ID for a vehicle */
  assetId?: string
  /** The current state of charge of the vehicle's battery */
  stateOfCharge?: number
  /** The timestamp at which the telematics data was recorded */
  telematicsTimestamp?: string
  /** The latitude of the vehicle's location at this point in time */
  latitude?: number
  /** The longitude of the vehicle's location at this point in time */
  longitude?: number
  /** The speed of the vehicle at this point in time */
  speed?: number
  /** The unit of measurement for the vehicle's speed */
  speedUnit?: string
  /** The total distance the vehicle has traveled */
  odometer?: number
  /** The unit of measurement for the vehicle's odometer */
  odometerUnit?: string
  /** The ambient air temperature at the vehicle's location */
  ambientAirTemperatureDegc?: number
  /** The aggregate energy consumed by the vehicle's HVAC system up until this point in time */
  hvacLifetimeWh?: number
  /** The aggregate energy regenerated by the vehicle's battery up until this point in time */
  batteryRegenLifetimeWh?: number
  /** The aggregate energy discharged by the vehicle's battery up until this point in time */
  batteryDischargeLifetimeWh?: number
  /** The aggregate energy regenerated by the vehicle's traction inverter up until this point in time */
  tracInverterRegenLifetimeWh?: number
  /** The aggregate energy consumed by the vehicle's traction inverter motor up until this point in time */
  tracInverterMotorLifetimeWh?: number
  /** The aggregate energy consumed by the vehicle's battery management system up until this point in time */
  batteryManagementLifetimeWh?: number
  /** The aggregate energy consumed by the vehicle's DC-DC converter up until this point in time */
  dcdcLifetimeWh?: number
  /** The potential of the vehicle's 12V system */
  batteryPotential?: number
  /** The state of the vehicle's key switch */
  keySwitchState?: string
  /** The gear the vehicle is currently in */
  gearSelect?: string
  /** The state of the vehicle's Electric Vehicle Supply Equipment (EVSE) */
  evseState?: string
  /** The power output of the vehicle's Electric Vehicle Supply Equipment (EVSE) */
  evsePowerKw?: number
  /** The estimated time remaining for the vehicle to be fully charged */
  chargeTimeEstimateMin?: number
  /** The Horizontal Dilution of Precision (HDOP) of the vehicle's GPS signal */
  gpsHdop?: number
  /** The estimated range remaining for the vehicle */
  rangeRemainingEstimateKm?: number
  /** The aggregate time the vehicle's key has been in the 'on' position up until this point in time */
  keyOnTimeHr?: number
  /** The aggregate time the vehicle has been in motion up until this point in time */
  drivingTimeHr?: number
  /** The current status of the vehicle */
  vehicleStatus?: 'In Motion' | 'Off' | 'Charging' | 'Plugged In' | 'Idle' | 'Unknown'
  /** The date the telematics data was recorded */
  date?: string
  /** The total distance the vehicle has driven over the last 24 hours */
  milesDriven?: number
  /** The total energy consumed by the vehicle over the last 24 hours */
  totalEnergyConsumedKwh?: number
  /** The energy consumed by the vehicle's HVAC system over the last 24 hours */
  hvacEnergyKwh?: number
  /** The energy consumed by the vehicle's motor over the last 24 hours */
  motorEnergyConsumedKwh?: number
  /** The energy regenerated by the vehicle over the last 24 hours */
  regenEnergyKwh?: number
  /** The energy consumed by the vehicle's DC-DC converter over the last 24 hours */
  dcdcEnergyKwh?: number
  /** The energy consumed by the vehicle's battery management system over the last 24 hours */
  batteryManagementEnergyKwh?: number
  /** The average ambient air temperature over the last 24 hours */
  airAmbientAvgTempC?: number
  /** The energy consumed by the vehicle's charging system over the last 24 hours */
  chargeEnergyKwh?: number
}
export type LatLngPair = {
  latitude?: number
  longitude?: number
}
export type Boundary = {
  latitude?: number
  longitude?: number
}
export type ReachableRange = {
  center?: LatLngPair
  boundary?: Boundary[]
}
export type AzureServiceableRangeResponse = {
  formatVersion?: string
  reachableRange?: ReachableRange
}
export type TimeUtilization = {
  totalDrivingTime?: SourceAttributeValueBigDecimal
  totalIdleTime?: SourceAttributeValueBigDecimal
  totalChargingTime?: SourceAttributeValueBigDecimal
  totalPluggedInTime?: SourceAttributeValueBigDecimal
}
export type EnergyUtilization = {
  totalMotorEnergyConsumed?: SourceAttributeValueBigDecimal
  totalRegenEnergyConsumed?: SourceAttributeValueBigDecimal
  totalDcdcEnergyConsumed?: SourceAttributeValueBigDecimal
  totalHvacEnergyConsumed?: SourceAttributeValueBigDecimal
  totalBatteryManagementEnergyConsumed?: SourceAttributeValueBigDecimal
}
export type VehicleMetricsModel = {
  vehicleId?: string
  distanceDriven?: SourceAttributeValueBigDecimal
  energyUsed?: SourceAttributeValueBigDecimal
  efficiency?: SourceAttributeValueBigDecimal
  carbonEmissionsSaved?: SourceAttributeValueBigDecimal
  averageTemperature?: SourceAttributeValueBigDecimal
  timeUtilization?: TimeUtilization
  energyUtilization?: EnergyUtilization
  fuelSaved?: SourceAttributeValueBigDecimal
  numVehicles?: number
  numDrivingDays?: number
  numTrips?: number
}
export type VehicleFaultModel = {
  id: string
  timestamp: string
  sourceAddress?: number
  sourceName?: string
  sourceLabel?: string
  spn?: number
  spnName?: string
  spnDescription?: string
  fmi?: number
  fmiText?: string
  occurrenceCount?: number
  milLampOn?: boolean
  amberLampOn?: boolean
  redLampOn?: boolean
  protectLampOn?: boolean
  dtcId?: string
  dtcShortCode?: string
  dtcDescription?: string
  latitude?: number
  longitude?: number
  provider: string
}
export type PagedResponseV1VehicleFaultModel = {
  meta: Meta
  payload: VehicleFaultModel[]
}
export type VehicleMetrics = {
  id?: string
  name?: string
  totalTrips?: number
  numDrivingDays?: number
  fuelSaved?: SourceAttributeValueBigDecimal
  energyUsed?: SourceAttributeValueBigDecimal
  efficiency?: SourceAttributeValueBigDecimal
  distanceDriven?: SourceAttributeValueBigDecimal
  carbonEmissionsSaved?: SourceAttributeValueBigDecimal
}
export type ReportGenerationStatus = {
  created?: string
  updated?: string
  id?: string
  status?: string
  reportUrl?: string
  requestedBy?: string
}
export type LatestVehicleStatusSummaryModel = {
  /** The total amount of vehicles queried. */
  total: number
  /** The home site ID of the vehicles queried. */
  siteId?: string
  /** A map indicating how many vehicles associated with a site, currently have a given status. */
  latestVehicleStatuses: {
    [key: string]: number
  }
}
export type PagedResponseV2VehicleAuthTagModel = {
  meta: Meta
  data: VehicleAuthTagModel[]
}
export type WebhookSubscriptionV2Model = {
  /** The organization id that the webhook subscription is created under */
  organizationId: string
  /** The name of the webhook subscription */
  name: string
  /** The endpoint for the webhook subscription for which the events will be sent to */
  endpoint: string
  events: (
    | 'charger.status_notification'
    | 'charger.boot_notification'
    | 'charger.transaction_event'
    | 'charger.data_transfer'
    | 'charger.ocpp_logs'
    | 'vehicle.telemetry_received'
    | 'vehicle.optimization'
  )[]
  /** The unique id for the webhook subscription */
  id: string
  /** The status for the webhook subscription */
  enabled: boolean
  /** Whether mTLS is enabled for this webhook subscription */
  mtlsEnabled?: boolean
  /** Whether OAuth client-credentials authentication is enabled for this webhook subscription */
  oauthEnabled?: boolean
}
export type PagedResponseV2WebhookSubscriptionV2Model = {
  meta: Meta
  data: WebhookSubscriptionV2Model[]
}
export type PagedResponseV2VehicleFaultModel = {
  meta: Meta
  data: VehicleFaultModel[]
}
export type UserSummaryModel = {
  /** Unique ID for a User */
  id?: string
  /** Full name of the user */
  name?: string
  /** Email address of the user */
  email?: string
  /** ID of the organization the user belongs to */
  organizationId?: string
  /** Name of the organization the user belongs to */
  organizationName?: string
  /** Roles assigned to the user */
  roles?: string[]
  /** Time of the user's most recent login (UTC) */
  lastLogin?: string
}
export type PagedResponseV2UserSummaryModel = {
  meta: Meta
  data: UserSummaryModel[]
}
export type RouteStopModel = {
  id?: string
  sequence?: number
  stopType?: string
  name?: string
  address?: string
  latitude?: number
  longitude?: number
  eta?: string
  departureTime?: string
  serviceDurationSeconds?: number
  distanceFromPreviousMeters?: number
  orderIds?: string[]
}
export type RouteDetailModel = {
  id?: string
  name?: string
  status?: string
  vehicleId?: string
  scheduledStart?: string
  scheduledEnd?: string
  actualStart?: string
  actualEnd?: string
  totalDistanceMeters?: number
  totalDurationSeconds?: number
  organizationId?: string
  created?: string
  updated?: string
  stops?: RouteStopModel[]
}
export type PagedResponseV2VehicleModel = {
  meta: Meta
  data: VehicleModel[]
}
export type SharingAgreementItemModel = {
  id?: string
  /** ORG, SITE, FLEET, ASSET */
  sharingType?: string
  assetId?: string
  assetName?: string
  /** PENDING, APPROVED, REVOKED, REJECTED */
  status?: string
}
export type SharingAgreementModel = {
  id?: string
  /** INITIATED, PENDING, OFFERED, ACTIVE, CANCELED */
  status?: string
  /** GRANTEE or GRANTOR */
  role?: string
  counterpartyOrgId?: string
  counterpartyOrgName?: string
  inviteCodeId?: string
  code?: string
  codeExpiresAt?: string
  /** Shared items in this agreement */
  items?: SharingAgreementItemModel[]
  initiatedAt?: string
  offeredAt?: string
  activeAt?: string
  canceledAt?: string
}
export type ChartSeriesV1Model = {
  /** Short unit symbol, e.g. "%", "mph", "kph", "F", "C", "mi", "km", "degrees" */
  unit?: string
  /** Sparse [epochMillis, value] pairs; timestamps where the signal has no value are omitted */
  points?: number[][]
}
export type OptionValue = {
  created?: string
  updated?: string
  optionType?: string
  value?: string
}
export type StripeIntentModel = {
  organizationId?: string
  organizationNm?: string
  customerId?: string
  intentId?: string
  clientSecret?: string
}
export type OcppTag = {
  entityId?: string
  entityName?: string
  entityType?: 'VEHICLE' | 'USER'
  externalId?: string
  tagId?: string
  ocppTagType?: 'MOBILE' | 'VID' | 'RFID' | 'QR_CODE' | 'NFC' | 'OTHER' | 'OPERATIONS'
}
export type Fleet = {
  created?: string
  updated?: string
  id?: string
  fleetNm?: string
  fleetType?: string
  active?: boolean
  organizationId?: string
}
export type OrganizationConfig = {
  created?: string
  updated?: string
  organizationId?: string
  configKey?: 'FROM_EMAIL' | 'SNS_PLATFORM_ARN'
  configValue?: string
}
export type ExternalOrganizationModel = {
  id?: string
  organizationNm?: string
}
export type LocaleModel = {
  /** The BCP 47 language tag for the locale */
  localeTag: string
  /** The name of the locale */
  localeName: string
  /** The measurement system for the locale */
  measurementSystem: 'SI' | 'US' | 'UK'
  /** The date format for the locale */
  dateFormat: 'DD/MM/YY' | 'MM/DD/YY'
  /** The time format for the locale */
  timeFormat: '12h' | '24h'
  /** The temperature unit for the locale */
  temperatureUnit: 'celsius' | 'fahrenheit'
}
export type Customer = {
  id?: string
  organizationNm?: string
  parentOrganizationId?: string
}
export type PagedResponseV2Customer = {
  meta: Meta
  data: Customer[]
}
export type ChartDataResponseModel = {
  units?: object[]
  data?: object[][]
}
export type BulkOperationMeta = {
  requestIndex?: number
}
export type MfaResetResponseData = {
  id?: string
}
export type BulkOperationSuccessMfaResetResponseData = {
  meta?: BulkOperationMeta
  data?: MfaResetResponseData
}
export type BulkOperationErrorDetail = {
  status?: number
  code?: string
  detail?: string
}
export type BulkOperationError = {
  meta?: BulkOperationMeta
  errors?: BulkOperationErrorDetail[]
}
export type BulkOperationResponseMfaResetResponseData = {
  successes?: BulkOperationSuccessMfaResetResponseData[]
  errors?: BulkOperationError[]
}
export type BulkMfaResetRequest = {
  userIds?: string[]
}
export const {
  useGetVehiclesQuery,
  useLazyGetVehiclesQuery,
  useUpdateVehicleMutation,
  useCreateVehicleWithMetadataMutation,
  useListWebhookSubscriptionsQuery,
  useLazyListWebhookSubscriptionsQuery,
  useUpdateWebhookSubscriptionsMutation,
  useCreateWebhookSubscriptionsMutation,
  useGetUserGroupQuery,
  useLazyGetUserGroupQuery,
  useUpdateUserGroupMutation,
  useDeleteUserGroupMutation,
  useUpdateMappingMutation,
  useUpdateUserMutation,
  useCreateUserMutation,
  useSignOutUserMutation,
  useResendMutation,
  useResetPasswordMutation,
  useMoveUserMutation,
  useEnableUserMutation,
  useDisableUserMutation,
  useUpdateScheduledRouteMutation,
  useCancelScheduledRouteMutation,
  useUpdateOrganizationMutation,
  useUpdateFleetTermsMutation,
  useSaveFleetTermsMutation,
  useGetLocationsOfInterestForOrganizationQuery,
  useLazyGetLocationsOfInterestForOrganizationQuery,
  useUpdateLocationOfInterestMutation,
  useCreateLocationOfInterestMutation,
  useGetFleetsQuery,
  useLazyGetFleetsQuery,
  useUpdateFleetMutation,
  useCreateFleetMutation,
  useAddVehicleEventMutation,
  useUpdateVehicleTelemetryMutation,
  useValidateTelematicsCredentialsMutation,
  useGetUserGroupsQuery,
  useLazyGetUserGroupsQuery,
  useCreateUserGroupMutation,
  useBackfillTicketPrefixesMutation,
  useGetStagedVehiclesQuery,
  useLazyGetStagedVehiclesQuery,
  useStageVehiclesMutation,
  useRevokeSharingMutation,
  useRejectOfferMutation,
  useAcceptOfferMutation,
  useRevokeInviteCodeMutation,
  useOfferOrgsMutation,
  useRedeemInviteCodeMutation,
  useGenerateInviteCodeMutation,
  useSaveOrganizationLogoVariantMutation,
  useDeleteOrganizationLogoVariantMutation,
  useSaveOrganizationIconVariantMutation,
  useDeleteOrganizationIconVariantMutation,
  useOnboardVehiclesMutation,
  useCreateMappingMutation,
  useInitiateMfaSetupQuery,
  useLazyInitiateMfaSetupQuery,
  useVerifySoftwareTokenMutation,
  useLookupByAuthProviderMutation,
  useGetOrCreateUserIdentityMutation,
  useCreateSimTripMutation,
  useCreateTripNoteMutation,
  useGetScheduledRoutesQuery,
  useLazyGetScheduledRoutesQuery,
  useCreateScheduledRouteMutation,
  useAssignVehicleMutation,
  useOnboardOrganizationToStripeMutation,
  useGetSsoConnectionDetailsQuery,
  useLazyGetSsoConnectionDetailsQuery,
  useCreateSsoConfigurationTicketMutation,
  useDeleteSsoConfigurationMutation,
  useTransferSsoConfigurationMutation,
  useSaveOrganizationLogoMutation,
  useDeleteOrganizationLogoMutation,
  useGetApiKeysByOrganizationIdQuery,
  useLazyGetApiKeysByOrganizationIdQuery,
  useProvisionApiKeyByOrganizationIdMutation,
  useGetIntegrationsByOrganizationIdQuery,
  useLazyGetIntegrationsByOrganizationIdQuery,
  useSaveOrganizationIntegrationMutation,
  useSaveOrganizationIconMutation,
  useDeleteOrganizationIconMutation,
  useGetOrganizationCustomersQuery,
  useLazyGetOrganizationCustomersQuery,
  useCreateOrganizationMutation,
  usePublishToSnsMutation,
  useCreateChargePortMutation,
  useAssignVehicleToBillingOrganizationMutation,
  usePatchOrganizationFeaturesMutation,
  useGetVehicleQuery,
  useLazyGetVehicleQuery,
  useGetLatestVehicleWeatherForecastQuery,
  useLazyGetLatestVehicleWeatherForecastQuery,
  useGetTripsQuery,
  useLazyGetTripsQuery,
  useGetTripQuery,
  useLazyGetTripQuery,
  useGetVehicleTelematicsQuery,
  useLazyGetVehicleTelematicsQuery,
  useGetServiceableRangeForVehicleQuery,
  useLazyGetServiceableRangeForVehicleQuery,
  useGetMetricsForVehicleQuery,
  useLazyGetMetricsForVehicleQuery,
  useGetVehicleFaultsQuery,
  useLazyGetVehicleFaultsQuery,
  useGetAssetEventsQuery,
  useLazyGetAssetEventsQuery,
  useGetVehicleStatusBreakdownForVehiclesQuery,
  useLazyGetVehicleStatusBreakdownForVehiclesQuery,
  useGetTimespanDataForVehiclesQuery,
  useLazyGetTimespanDataForVehiclesQuery,
  useGetVehiclesBySiteQuery,
  useLazyGetVehiclesBySiteQuery,
  useGetVehicleReportQuery,
  useLazyGetVehicleReportQuery,
  useGetLatestVehicleStatusesQuery,
  useLazyGetLatestVehicleStatusesQuery,
  useGetMetricsForVehiclesQuery,
  useLazyGetMetricsForVehiclesQuery,
  useGetVehicleAuthTagsQuery,
  useLazyGetVehicleAuthTagsQuery,
  useListWebhookSubscriptions1Query,
  useLazyListWebhookSubscriptions1Query,
  useGetWebhookSubscriptionQuery,
  useLazyGetWebhookSubscriptionQuery,
  useGetWebhookSubscription1Query,
  useLazyGetWebhookSubscription1Query,
  useDeleteWebhookSubscriptionMutation,
  useGetVehicleFaults1Query,
  useLazyGetVehicleFaults1Query,
  useGetUsersQuery,
  useLazyGetUsersQuery,
  useGetDescartesRouteByIdQuery,
  useLazyGetDescartesRouteByIdQuery,
  useGetVehiclesForOrganizationV1Query,
  useLazyGetVehiclesForOrganizationV1Query,
  useGetActiveCodesQuery,
  useLazyGetActiveCodesQuery,
  useGetGrantorSharesQuery,
  useLazyGetGrantorSharesQuery,
  useGetGrantorPendingQuery,
  useLazyGetGrantorPendingQuery,
  useGetGranteeSharesQuery,
  useLazyGetGranteeSharesQuery,
  useGetSharingAgreementsQuery,
  useLazyGetSharingAgreementsQuery,
  useGetChartValuesV1Query,
  useLazyGetChartValuesV1Query,
  useGetUserQuery,
  useLazyGetUserQuery,
  useGetUserTagMappingsQuery,
  useLazyGetUserTagMappingsQuery,
  useGetDropdownOptionsQuery,
  useLazyGetDropdownOptionsQuery,
  useGetTripNotesQuery,
  useLazyGetTripNotesQuery,
  useGetTripReportQuery,
  useLazyGetTripReportQuery,
  useGetStatusOfGeneratedReportQuery,
  useLazyGetStatusOfGeneratedReportQuery,
  useGetOrganizationQuery,
  useLazyGetOrganizationQuery,
  useGetVehiclesForOrganizationQuery,
  useLazyGetVehiclesForOrganizationQuery,
  useGetOrganizationUsersQuery,
  useLazyGetOrganizationUsersQuery,
  useGetTrips1Query,
  useLazyGetTrips1Query,
  useGetOrganizationTermsQuery,
  useLazyGetOrganizationTermsQuery,
  useLoginToStripeQuery,
  useLazyLoginToStripeQuery,
  useGetStripeIntentDetailQuery,
  useLazyGetStripeIntentDetailQuery,
  useGetRoutesForOrganizationQuery,
  useLazyGetRoutesForOrganizationQuery,
  useGetIntegrationsByOrganizationIdAndIntegrationTypeQuery,
  useLazyGetIntegrationsByOrganizationIdAndIntegrationTypeQuery,
  useGetAllOcppTagsQuery,
  useLazyGetAllOcppTagsQuery,
  useGetOrganizationHierarchyQuery,
  useLazyGetOrganizationHierarchyQuery,
  useGetFleets1Query,
  useLazyGetFleets1Query,
  useGetAllOrganizationCustomersQuery,
  useLazyGetAllOrganizationCustomersQuery,
  useGetOrganizationConfigQuery,
  useLazyGetOrganizationConfigQuery,
  useGetOrganizationApiKeyByApiKeyQuery,
  useLazyGetOrganizationApiKeyByApiKeyQuery,
  useGetCurrentOrganizationQuery,
  useLazyGetCurrentOrganizationQuery,
  useGetLocationOfInterestQuery,
  useLazyGetLocationOfInterestQuery,
  useDeleteLocationOfInterestMutation,
  useGetLocalesQuery,
  useLazyGetLocalesQuery,
  useGetFleetQuery,
  useLazyGetFleetQuery,
  useDeleteFleetMutation,
  useGetCustomersQuery,
  useLazyGetCustomersQuery,
  useGetChartValuesQuery,
  useLazyGetChartValuesQuery,
  useBulkResetMfaMutation,
  useDeleteChargePortMutation
} = injectedRtkApi
